import { useState, useEffect } from "react";
import { jsPDF } from "jspdf";

const SAVE_KEY = "beechcroft_college_calculator";

const defaultSchool = () => ({
  id: Date.now() + Math.random(),
  name: "",
  tuition: "",
  roomBoard: "",
  fees: "",
  books: "",
  transportation: "",
  personalMisc: "",
  grants: "",
  scholarships: "",
  workStudy: "",
  outsideAid: [{ id: Date.now() + Math.random(), label: "", amount: "" }],
  iKnowICan: false,
  subsidizedToggle: false,
  unsubsidizedToggle: false,
  loanFees: "",
  subsidizedLoans: "",
  unsubsidizedLoans: "",
  stateGrant: "",
  notes: "",
});

const fmt = (val) =>
  (parseFloat(val) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const num = (v) => parseFloat(v) || 0;
const LOAN_RATE = 0.0653;

function calcSchool(s) {
  const directCosts = num(s.tuition) + num(s.roomBoard) + num(s.fees);
  const indirectCosts = num(s.books) + num(s.transportation) + num(s.personalMisc);
  const totalCOA = directCosts + indirectCosts;
  const outsideAidTotal = (s.outsideAid || []).reduce((sum, item) => sum + num(item.amount), 0);
  const iKnowICanGrant = s.iKnowICan ? 2500 : 0;
  const freeAid = num(s.grants) + num(s.scholarships) + num(s.stateGrant) + outsideAidTotal + iKnowICanGrant;
  const workStudyAid = num(s.workStudy);
  const loanFees = num(s.loanFees);
  const loans = num(s.subsidizedLoans) + num(s.unsubsidizedLoans);
  const netCost = Math.max(0, totalCOA - freeAid - workStudyAid);
  const netDirectCost = Math.max(0, directCosts - freeAid - workStudyAid);
  const totalAid = freeAid + workStudyAid + loans;
  const refund = totalAid > totalCOA ? totalAid - totalCOA : 0;
  const outOfPocket = (totalAid >= totalCOA ? 0 : Math.max(0, totalCOA - totalAid)) + loanFees;
  const fourYearCOA = totalCOA * 4;
  const fourYearFreeAid = freeAid * 4;
  const fourYearLoans = loans * 4;
  const fourYearLoanFees = loanFees * 4;
  const fourYearOOP = outOfPocket * 4;
  const fourYearRefund = refund * 4;
  const totalLoanDebt = fourYearLoans + fourYearLoanFees;
  const monthlyRate = LOAN_RATE / 12;
  const n = 120;
  const monthlyPayment = totalLoanDebt > 0
    ? (totalLoanDebt * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1)
    : 0;
  return { directCosts, indirectCosts, totalCOA, freeAid, workStudyAid, loans, loanFees, netCost, netDirectCost, outOfPocket, refund, fourYearCOA, fourYearFreeAid, fourYearLoans, fourYearLoanFees, totalLoanDebt, fourYearOOP, fourYearRefund, monthlyPayment };
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);
  return width;
}

const COLORS = ["#1C3A5E", "#0F6E56", "#7C3AED", "#DC6B2F", "#1D6FB4", "#B8904A", "#0E7490"];
const LIGHT_COLORS = ["#EEF4FB", "#ECFDF5", "#F5F3FF", "#FEF3EB", "#EFF6FF", "#FDF8EE", "#ECFEFF"];
const named = (s) => s.name || "Unnamed School";


const OHIO_SCHOOLS = [
  // ── A ──
  { name: "Allegheny College", dataYear: "2026-27", tuition: 59050, roomBoard: 14874, fees: 740, books: 500, transportation: 1350, personalMisc: 1250 },
  { name: "Ashland University", dataYear: "2025-26", tuition: 33220, roomBoard: 13994, fees: 0, books: 1120, transportation: 1200, personalMisc: 7114 },
  { name: "Avila University", dataYear: "2025-26", tuition: 41540, roomBoard: 12352, fees: 0, books: 1244, transportation: 0, personalMisc: 2380 },
  // ── B ──
  { name: "Belmont University", dataYear: "2025-26", tuition: 43750, roomBoard: 22710, fees: 0, books: 1500, transportation: 0, personalMisc: 6000 },
  { name: "Bennett College for Women", dataYear: "2025-26", tuition: 21280, roomBoard: 9652, fees: 0, books: 422, transportation: 250, personalMisc: 12500 },
  { name: "Berklee College of Music", dataYear: "2025-26", tuition: 54000, roomBoard: 21900, fees: 0, books: 1220, transportation: 1140, personalMisc: 4042 },
  { name: "Bowie State University", dataYear: "2025-26", tuition: 20464, roomBoard: 12441, fees: 0, books: 1250, transportation: 2224, personalMisc: 1756 },
  { name: "Bowling Green State University - Firelands Campus", dataYear: "2025-26", tuition: 6526, roomBoard: 13730, fees: 0, books: 760, transportation: 1810, personalMisc: 2690 },
  { name: "Bowling Green State University - Main Campus", dataYear: "2025-26", tuition: 14482, roomBoard: 14160, fees: 0, books: 760, transportation: 1110, personalMisc: 2690 },
  // ── C ──
  { name: "Capital University (On-Campus)", dataYear: "2026-27", tuition: 45600, roomBoard: 13800, fees: 102, books: 2238, transportation: 808, personalMisc: 1278 },
  { name: "Capital University (Off-Campus)", dataYear: "2026-27", tuition: 45600, roomBoard: 12710, fees: 102, books: 2238, transportation: 2688, personalMisc: 2556 },
  { name: "Capital University (With Parent)", dataYear: "2026-27", tuition: 45600, roomBoard: 6084, fees: 102, books: 2238, transportation: 2688, personalMisc: 1278 },
  { name: "Central Ohio Technical College", dataYear: "2025-26", tuition: 4536, roomBoard: 13568, fees: 0, books: 1320, transportation: 3850, personalMisc: 0 },
  { name: "Central State University", dataYear: "2026-27", tuition: 8300, roomBoard: 13370, fees: 0, books: 720, transportation: 1600, personalMisc: 0 },
  { name: "Clark Atlanta University", dataYear: "2025-26", tuition: 26886, roomBoard: 18750, fees: 0, books: 988, transportation: 1500, personalMisc: 5234 },
  { name: "Coastal Carolina University", dataYear: "2025-26", tuition: 29926, roomBoard: 12378, fees: 0, books: 1294, transportation: 0, personalMisc: 4388 },
  { name: "College of Wooster", dataYear: "2025-26", tuition: 64210, roomBoard: 15350, fees: 0, books: 1250, transportation: 0, personalMisc: 1000 },
  { name: "Columbia College Chicago", dataYear: "2025-26", tuition: 35732, roomBoard: 23884, fees: 0, books: 1950, transportation: 980, personalMisc: 2406 },
  { name: "Columbus College of Art and Design", dataYear: "2025-26", tuition: 42995, roomBoard: 8250, fees: 0, books: 5050, transportation: 1400, personalMisc: 2550 },
  { name: "Columbus State Community College (With Parent)", dataYear: "2026-27", tuition: 4630, roomBoard: 4455, fees: 56, books: 2000, transportation: 2700, personalMisc: 2205 },
  { name: "Coppin State University", dataYear: "2025-26", tuition: 7198, roomBoard: 15934, fees: 0, books: 1900, transportation: 3000, personalMisc: 3516 },
  { name: "Cornell University", dataYear: "2025-26", tuition: 71266, roomBoard: 20574, fees: 0, books: 1216, transportation: 670, personalMisc: 3252 },
  // ── D ──
  { name: "Defiance College", dataYear: "2025-26", tuition: 28500, roomBoard: 13043, fees: 0, books: 1400, transportation: 1000, personalMisc: 3177 },
  { name: "Denison University", dataYear: "2026-27", tuition: 74000, roomBoard: 18400, fees: 0, books: 2250, transportation: 0, personalMisc: 2250 },
  // ── E ──
  { name: "Embry-Riddle Aeronautical University-Prescott", dataYear: "2025-26", tuition: 45766, roomBoard: 15864, fees: 0, books: 1300, transportation: 1700, personalMisc: 2070 },
  // ── F ──
  { name: "Fashion Institute of Technology", dataYear: "2026-27", tuition: 17514, roomBoard: 26575, fees: 0, books: 1850, transportation: 2400, personalMisc: 1870 },
  { name: "Florida Memorial University", dataYear: "2025-26", tuition: 7137, roomBoard: 4652, fees: 0, books: 600, transportation: 1070, personalMisc: 3880 },
  { name: "Franklin University", dataYear: "2025-26", tuition: 7152, roomBoard: 15120, fees: 0, books: 576, transportation: 2664, personalMisc: 3872 },
  // ── G ──
  { name: "Gannon University", dataYear: "2025-26", tuition: 40635, roomBoard: 16425, fees: 0, books: 1284, transportation: 1436, personalMisc: 1604 },
  { name: "Georgetown University", dataYear: "2025-26", tuition: 71338, roomBoard: 21202, fees: 0, books: 1000, transportation: 420, personalMisc: 2172 },
  // ── H ──
  { name: "Hampton University", dataYear: "2026-27", tuition: 33086, roomBoard: 15736, fees: 0, books: 1000, transportation: 1300, personalMisc: 1500 },
  { name: "Hocking College", dataYear: "2025-26", tuition: 5300, roomBoard: 14260, fees: 0, books: 600, transportation: 0, personalMisc: 0 },
  { name: "Howard University", dataYear: "2026-27", tuition: 38936, roomBoard: 21048, fees: 0, books: 1220, transportation: 1154, personalMisc: 2544 },
  // ── I ──
  { name: "Illinois Institute of Technology", dataYear: "2025-26", tuition: 53398, roomBoard: 18130, fees: 0, books: 1240, transportation: 1300, personalMisc: 3556 },
  // ── J ──
  { name: "Jackson State University", dataYear: "2025-26", tuition: 12890, roomBoard: 12073, fees: 0, books: 1442, transportation: 3991, personalMisc: 5618 },
  { name: "Johns Hopkins University", dataYear: "2026-27", tuition: 68670, roomBoard: 21967, fees: 500, books: 1377, transportation: 566, personalMisc: 1606 },
  // ── K ──
  { name: "Kent State University", dataYear: "2026-27", tuition: 14218, roomBoard: 14380, fees: 0, books: 800, transportation: 2620, personalMisc: 2334 },
  { name: "Kenyon College", dataYear: "2026-27", tuition: 73230, roomBoard: 19860, fees: 0, books: 1900, transportation: 0, personalMisc: 1950 },
  { name: "Kentucky State University", dataYear: "2025-26", tuition: 13932, roomBoard: 15898, fees: 0, books: 1592, transportation: 2958, personalMisc: 1734 },
  // ── L ──
  { name: "Lindsey Wilson University", dataYear: "2025-26", tuition: 27808, roomBoard: 10178, fees: 0, books: 900, transportation: 2000, personalMisc: 6188 },
  { name: "Louisiana State University A&M", dataYear: "2025-26", tuition: 29148, roomBoard: 15404, fees: 0, books: 1084, transportation: 3840, personalMisc: 4044 },
  // ── M ──
  { name: "Miami University-Oxford", dataYear: "2026-27", tuition: 19070, roomBoard: 17832, fees: 46, books: 1330, transportation: 1380, personalMisc: 2680 },
  { name: "Michigan State University", dataYear: "2026-27", tuition: 46034, roomBoard: 12942, fees: 0, books: 1420, transportation: 2620, personalMisc: 3930 },
  { name: "Minneapolis College of Art and Design", dataYear: "2025-26", tuition: 47264, roomBoard: 16900, fees: 0, books: 3800, transportation: 0, personalMisc: 3295 },
  { name: "Morehouse College", dataYear: "2025-26", tuition: 34055, roomBoard: 14984, fees: 0, books: 2000, transportation: 1368, personalMisc: 3956 },
  { name: "Morgan State University", dataYear: "2025-26", tuition: 19456, roomBoard: 19720, fees: 0, books: 2000, transportation: 1000, personalMisc: 2030 },
  { name: "Mount Carmel College of Nursing", dataYear: "2025-26", tuition: 15000, roomBoard: 6380, fees: 0, books: 1500, transportation: 1980, personalMisc: 3240 },
  { name: "Mount St. Joseph University", dataYear: "2025-26", tuition: 38400, roomBoard: 12130, fees: 0, books: 1200, transportation: 600, personalMisc: 1850 },
  // ── N ──
  { name: "North Carolina A & T State University", dataYear: "2026-27", tuition: 21578, roomBoard: 12068, fees: 0, books: 800, transportation: 1780, personalMisc: 3386 },
  { name: "North Carolina Central University", dataYear: "2025-26", tuition: 20522, roomBoard: 8544, fees: 0, books: 1500, transportation: 600, personalMisc: 5156 },
  { name: "Northeastern University", dataYear: "2026-27", tuition: 72744, roomBoard: 22820, fees: 60, books: 1000, transportation: 900, personalMisc: 900 },
  // ── O ──
  { name: "Oberlin College", dataYear: "2025-26", tuition: 69482, roomBoard: 20096, fees: 0, books: 930, transportation: 0, personalMisc: 978 },
  { name: "Ohio Dominican University", dataYear: "2025-26", tuition: 36422, roomBoard: 13262, fees: 0, books: 1080, transportation: 500, personalMisc: 1582 },
  { name: "Ohio University", dataYear: "2026-27", tuition: 15018, roomBoard: 15320, fees: 0, books: 918, transportation: 2016, personalMisc: 1022 },
  { name: "Ohio Wesleyan University", dataYear: "2026-27", tuition: 56040, roomBoard: 15882, fees: 0, books: 1150, transportation: 670, personalMisc: 1880 },
  { name: "Otterbein University (Off-Campus)", dataYear: "2026-27", tuition: 36670, roomBoard: 2946, fees: 0, books: 1594, transportation: 2000, personalMisc: 2023 },
  // ── P ──
  { name: "Parsons the New School for Design", dataYear: "2025-26", tuition: 61650, roomBoard: 27514, fees: 0, books: 1400, transportation: 1188, personalMisc: 1540 },
  { name: "Purdue University", dataYear: "2025-26", tuition: 30844, roomBoard: 13720, fees: 0, books: 1090, transportation: 420, personalMisc: 2216 },
  // ── R ──
  { name: "Rochester Institute of Technology", dataYear: "2026-27", tuition: 65038, roomBoard: 17370, fees: 0, books: 2890, transportation: 0, personalMisc: 0 },
  { name: "Roger Williams University", dataYear: "2026-27", tuition: 47872, roomBoard: 18632, fees: 70, books: 1000, transportation: 1600, personalMisc: 2100 },
  // ── S ──
  { name: "Shawnee State University", dataYear: "2025-26", tuition: 10448, roomBoard: 12570, fees: 0, books: 1024, transportation: 1500, personalMisc: 1656 },
  { name: "Southern Illinois University Carbondale", dataYear: "2025-26", tuition: 13544, roomBoard: 13015, fees: 0, books: 780, transportation: 1300, personalMisc: 1761 },
  { name: "Sullivan University", dataYear: "2025-26", tuition: 11082, roomBoard: 6990, fees: 0, books: 1880, transportation: 0, personalMisc: 3559 },
  // ── T ──
  { name: "The Ohio State University-Main Campus (On-Campus)", dataYear: "2026-27", tuition: 13902, roomBoard: 15630, fees: 40, books: 1020, transportation: 732, personalMisc: 2686 },
  { name: "The Ohio State University-Main Campus (Off-Campus)", dataYear: "2026-27", tuition: 13244, roomBoard: 5818, fees: 0, books: 1020, transportation: 2664, personalMisc: 2918 },
  { name: "The Ohio State University-Newark Campus (On-Campus)", dataYear: "2025-26", tuition: 9772, roomBoard: 10888, fees: 0, books: 1020, transportation: 1386, personalMisc: 2958 },
  { name: "The Ohio State University-Newark Campus (Off-Campus)", dataYear: "2026-27", tuition: 9968, roomBoard: 4964, fees: 40, books: 1020, transportation: 4176, personalMisc: 2918 },
  { name: "Tiffin University", dataYear: "2025-26", tuition: 34500, roomBoard: 12882, fees: 0, books: 3000, transportation: 1000, personalMisc: 2091 },
  { name: "Tuskegee University", dataYear: "2025-26", tuition: 20226, roomBoard: 9844, fees: 0, books: 1812, transportation: 2874, personalMisc: 10442 },
  // ── U ──
  { name: "University of Akron", dataYear: "2026-27", tuition: 6870, roomBoard: 13162, fees: 0, books: 1200, transportation: 2000, personalMisc: 1596 },
  { name: "University of Arkansas at Pine Bluff", dataYear: "2025-26", tuition: 31550, roomBoard: 14968, fees: 0, books: 1216, transportation: 2640, personalMisc: 3370 },
  { name: "University of Cincinnati-Blue Ash College", dataYear: "2025-26", tuition: 7200, roomBoard: 17999, fees: 0, books: 1300, transportation: 2038, personalMisc: 4892 },
  { name: "University of Cincinnati-Main", dataYear: "2026-27", tuition: 14776, roomBoard: 14518, fees: 0, books: 1300, transportation: 2038, personalMisc: 3386 },
  { name: "University of Dayton", dataYear: "2025-26", tuition: 50610, roomBoard: 17610, fees: 0, books: 0, transportation: 1100, personalMisc: 2045 },
  { name: "University of Findlay", dataYear: "2025-26", tuition: 41400, roomBoard: 11700, fees: 0, books: 1000, transportation: 1000, personalMisc: 1504 },
  { name: "University of Kentucky", dataYear: "2025-26", tuition: 13908, roomBoard: 14510, fees: 0, books: 1200, transportation: 2675, personalMisc: 3232 },
  { name: "University of Maryland-College Park", dataYear: "2025-26", tuition: 42052, roomBoard: 17106, fees: 0, books: 1250, transportation: 1514, personalMisc: 1200 },
  { name: "University of Michigan", dataYear: "2026-27", tuition: 63962, roomBoard: 16246, fees: 0, books: 1184, transportation: 400, personalMisc: 2372 },
  { name: "University of Minnesota - Twin Cities", dataYear: "2025-26", tuition: 41512, roomBoard: 14672, fees: 0, books: 1000, transportation: 1700, personalMisc: 2158 },
  { name: "University of Toledo", dataYear: "2025-26", tuition: 13426, roomBoard: 16449, fees: 0, books: 1290, transportation: 914, personalMisc: 2744 },
  // ── V ──
  { name: "Virginia State University", dataYear: "2025-26", tuition: 23404, roomBoard: 14164, fees: 0, books: 1300, transportation: 1250, personalMisc: 2390 },
  // ── W ──
  { name: "Walsh University", dataYear: "2025-26", tuition: 36800, roomBoard: 15552, fees: 0, books: 790, transportation: 1190, personalMisc: 1888 },
  { name: "Waynesburg University", dataYear: "2025-26", tuition: 31580, roomBoard: 13570, fees: 0, books: 1500, transportation: 780, personalMisc: 250 },
  { name: "West Kentucky Community & Technical College", dataYear: "2025-26", tuition: 4872, roomBoard: 8814, fees: 0, books: 1268, transportation: 2590, personalMisc: 1066 },
  { name: "West Virginia Wesleyan College", dataYear: "2025-26", tuition: 34416, roomBoard: 13306, fees: 0, books: 1500, transportation: 1000, personalMisc: 2565 },
  { name: "Wilberforce University", dataYear: "2025-26", tuition: 16410, roomBoard: 9110, fees: 0, books: 1200, transportation: 0, personalMisc: 2000 },
  { name: "Wilmington College", dataYear: "2025-26", tuition: 32082, roomBoard: 11923, fees: 0, books: 1000, transportation: 700, personalMisc: 0 },
  { name: "Winston-Salem State University", dataYear: "2025-26", tuition: 16784, roomBoard: 12360, fees: 0, books: 1000, transportation: 3490, personalMisc: 2064 },
  { name: "Wittenberg University", dataYear: "2026-27", tuition: 47998, roomBoard: 12800, fees: 0, books: 1250, transportation: 1000, personalMisc: 2250 },
  { name: "Wright State University - Lake Campus", dataYear: "2025-26", tuition: 9308, roomBoard: 5582, fees: 0, books: 1400, transportation: 0, personalMisc: 200 },
  { name: "Wright State University - Main Campus", dataYear: "2025-26", tuition: 12200, roomBoard: 10240, fees: 0, books: 1400, transportation: 1800, personalMisc: 572 },
  // ── X ──
  { name: "Xavier University", dataYear: "2025-26", tuition: 54035, roomBoard: 14520, fees: 0, books: 500, transportation: 1700, personalMisc: 1500 },
  { name: "Xavier University (OH)", dataYear: "2026-27", tuition: 54255, roomBoard: 14520, fees: 58, books: 500, transportation: 1700, personalMisc: 1500 },
  { name: "Xavier University of Louisiana", dataYear: "2025-26", tuition: 33634, roomBoard: 12126, fees: 0, books: 1384, transportation: 3650, personalMisc: 2664 },
  // ── Y ──
  { name: "Youngstown State University", dataYear: "2025-26", tuition: 12600, roomBoard: 11730, fees: 0, books: 1200, transportation: 2144, personalMisc: 2546 },
];

function exportPDF(schools, studentName) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = 0;

  const newPage = () => { doc.addPage(); y = 16; };
  const checkY = (needed = 12) => { if (y + needed > 278) newPage(); };

  const sectionLabel = (text) => {
    checkY(8);
    doc.setFontSize(7); doc.setFont("helvetica", "bold");
    doc.setTextColor(28, 58, 94);
    doc.text(text.toUpperCase(), margin + 2, y);
    y += 5;
  };

  const row = (label, value, opts = {}) => {
    checkY(7);
    const { bold = false, indent = 0, rColor = [100,116,139], lColor = null } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(8.5);
    const lc = lColor || (bold ? [15,23,42] : [71,85,105]);
    doc.setTextColor(...lc);
    doc.text(label, margin + 2 + indent, y);
    doc.setTextColor(...rColor);
    doc.text(value, pageW - margin - 2, y, { align: "right" });
    y += 5.5;
  };

  const divider = (light = false) => {
    checkY(4);
    doc.setDrawColor(...(light ? [226,232,240] : [203,213,225]));
    doc.setLineWidth(light ? 0.2 : 0.4);
    doc.line(margin + 2, y, pageW - margin - 2, y);
    y += 3.5;
  };

  const filledBox = (fillRGB, height, draw) => {
    checkY(height + 4);
    doc.setFillColor(...fillRGB);
    doc.roundedRect(margin, y, contentW, height, 2, 2, "F");
    draw(y);
    y += height + 5;
  };

  const pageHeader = (schoolName, idx) => {
    doc.setFillColor(28, 58, 94);
    doc.rect(0, 0, pageW, 18, "F");
    doc.setFontSize(14); doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("College Costs", margin, 12);
    doc.setFillColor(240, 192, 96);
    doc.rect(margin + 65, 9, contentW - 65, 1.2, "F");
    if (studentName && studentName.trim()) {
      doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.setTextColor(186, 230, 253);
      doc.text(`Prepared for: ${studentName.trim()}`, pageW - margin, 8, { align: "right" });
    }
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
    doc.setTextColor(148, 186, 220);
    doc.text(`Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`, pageW - margin, 14, { align: "right" });
    y = 24;
    doc.setFillColor(13, 33, 55);
    doc.roundedRect(margin, y, contentW, 11, 2, 2, "F");
    doc.setFillColor(240, 192, 96);
    doc.circle(margin + 5, y + 5.5, 2.5, "F");
    doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(schoolName, margin + 11, y + 7.5);
    doc.setFontSize(7); doc.setTextColor(186, 230, 253);
    doc.text(`School ${idx + 1} of ${schools.length}`, pageW - margin - 2, y + 7.5, { align: "right" });
    y += 16;
  };

  schools.forEach((s, idx) => {
    if (idx > 0) doc.addPage();
    pageHeader(named(s), idx);
    const c = calcSchool(s);
    const schoolGrants = num(s.grants) + num(s.scholarships);
    const stateGrant = num(s.stateGrant);
    const outsideAidTotal = (s.outsideAid || []).reduce((sum, a) => sum + num(a.amount), 0);
    const iKnowICanAmt = s.iKnowICan ? 2500 : 0;

    sectionLabel("Cost of Attendance");
    row("Tuition & Fees", fmt(num(s.tuition) + num(s.fees)), { lColor: [51,65,85] });
    row("Room & Board", fmt(num(s.roomBoard)), { lColor: [51,65,85] });
    row("Books & Supplies", fmt(num(s.books)), { lColor: [100,116,139] });
    row("Transportation", fmt(num(s.transportation)), { lColor: [100,116,139] });
    row("Personal & Misc.", fmt(num(s.personalMisc)), { lColor: [100,116,139] });
    divider();
    row("Direct Costs (billed by school)", fmt(c.directCosts), { bold: true, rColor: [239,68,68] });
    row("Indirect Costs (living expenses)", fmt(c.indirectCosts), { rColor: [245,158,11] });
    divider();
    row("Total Cost of Attendance", fmt(c.totalCOA), { bold: true, rColor: [15,23,42] });
    y += 2;

    sectionLabel("Financial Aid Package");
    if (schoolGrants > 0) {
      row("Institutional Grants", fmt(num(s.grants)), { rColor: [16,185,129] });
      row("Federal Grants (Pell, etc.)", fmt(num(s.scholarships)), { rColor: [16,185,129] });
    }
    if (stateGrant > 0) row("State Grant (OCOG/Other)", fmt(stateGrant), { rColor: [16,185,129] });
    if (outsideAidTotal > 0) {
      (s.outsideAid || []).filter(a => num(a.amount) > 0).forEach(a => {
        row(a.label || "Outside Scholarship", fmt(num(a.amount)), { indent: 2, rColor: [16,185,129] });
      });
    }
    if (iKnowICanAmt > 0) row("I Know I Can Grant", fmt(iKnowICanAmt), { rColor: [16,185,129] });
    if (c.workStudyAid > 0) row("Work-Study (earned)", fmt(c.workStudyAid), { rColor: [59,130,246] });
    divider(true);
    row("Total Free Aid", fmt(c.freeAid + c.workStudyAid), { bold: true, rColor: [16,185,129] });
    y += 2;

    if (num(s.subsidizedLoans) > 0 || num(s.unsubsidizedLoans) > 0 || num(s.loanFees) > 0) {
      doc.setFontSize(7.5); doc.setFont("helvetica", "italic");
      doc.setTextColor(239, 68, 68);
      doc.text("Loans — must be repaid with interest", margin + 2, y);
      y += 5;
      if (num(s.subsidizedLoans) > 0) row("Subsidized Loan", fmt(num(s.subsidizedLoans)), { indent: 2, rColor: [239,68,68] });
      if (num(s.unsubsidizedLoans) > 0) row("Unsubsidized Loan", fmt(num(s.unsubsidizedLoans)), { indent: 2, rColor: [239,68,68] });
      if (num(s.loanFees) > 0) row("Loan Origination Fees", fmt(num(s.loanFees)), { indent: 2, rColor: [239,68,68] });
      divider(true);
      row("Total Loans", fmt(c.loans), { bold: true, rColor: [239,68,68] });
      y += 2;
    }

    sectionLabel("Net Cost Summary");
    row("Total Cost of Attendance", fmt(c.totalCOA));
    row("Minus: All Free Aid", `- ${fmt(c.freeAid + c.workStudyAid)}`, { rColor: [16,185,129] });
    if (c.loans > 0) row("Minus: Loans Applied", `- ${fmt(c.loans)}`, { rColor: [239,68,68] });
    if (c.loanFees > 0) row("Minus: Loan Fees Applied", `- ${fmt(c.loanFees)}`, { rColor: [239,68,68] });
    divider(true);
    const netCostAfterAll = Math.max(0, c.totalCOA - c.freeAid - c.workStudyAid - c.loans - c.loanFees);
    row("Net Cost After All Aid (incl. loans)", fmt(netCostAfterAll), { bold: true, rColor: [245,158,11] });
    if (c.loans > 0 || c.loanFees > 0) {
      checkY(6);
      doc.setFontSize(7); doc.setFont("helvetica", "italic");
      doc.setTextColor(239, 68, 68);
      doc.text("  * Loans must be repaid — they are not free money", margin + 6, y);
      y += 5;
    }
    y += 2;

    // Full Cost of Attendance box
    filledBox([238, 244, 251], 26, (boxY) => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
      doc.setTextColor(28, 58, 94);
      doc.text("FULL COST / YEAR", margin + 4, boxY + 7);
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("School bill + living expenses (books, transport, personal)", margin + 4, boxY + 13);
      doc.setFontSize(15); doc.setFont("helvetica", "bold");
      doc.setTextColor(28, 58, 94);
      doc.text(fmt(c.outOfPocket), pageW - margin - 4, boxY + 10, { align: "right" });
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(`4-Year: ${fmt(c.fourYearOOP)}`, pageW - margin - 4, boxY + 18, { align: "right" });
    });

    // School Bill Only box
    const schoolBillOnly = Math.max(0, c.netDirectCost - c.loans - c.loanFees);
    filledBox([228, 238, 250], 26, (boxY) => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5);
      doc.setTextColor(28, 58, 94);
      doc.text("SCHOOL BILL ONLY / YEAR", margin + 4, boxY + 7);
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text("Tuition, room & board, fees — what the school will actually charge you", margin + 4, boxY + 13);
      doc.setFontSize(15); doc.setFont("helvetica", "bold");
      doc.setTextColor(28, 58, 94);
      doc.text(fmt(schoolBillOnly), pageW - margin - 4, boxY + 10, { align: "right" });
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(`4-Year: ${fmt(schoolBillOnly * 4)}`, pageW - margin - 4, boxY + 18, { align: "right" });
    });

    if (c.refund > 0) {
      filledBox([236, 253, 245], 16, (boxY) => {
        doc.setFont("helvetica", "bold"); doc.setFontSize(8.5);
        doc.setTextColor(16, 185, 129);
        doc.text(`Est. Refund / Year: ${fmt(c.refund)}`, margin + 4, boxY + 7);
        doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text("Loan refunds must be repaid with interest", margin + 4, boxY + 12);
      });
    }

    if (c.totalLoanDebt > 0 || c.fourYearCOA > 0) {
      sectionLabel("4-Year Projection");
      row("4-Year Total Cost of Attendance", fmt(c.fourYearCOA));
      row("4-Year Total Free Aid", `- ${fmt(c.fourYearFreeAid)}`, { rColor: [16,185,129] });
      row("4-Year Total Out-of-Pocket", fmt(c.fourYearOOP), { bold: true, rColor: [28,58,94] });
      if (c.totalLoanDebt > 0) {
        divider(true);
        row("Total Loan Debt at Graduation", fmt(c.totalLoanDebt), { bold: true, rColor: [239,68,68] });
        row("Est. Monthly Payment (10-yr plan @ 6.53%)", fmt(c.monthlyPayment) + "/mo", { rColor: [239,68,68] });
      }
      y += 2;
    }

    if (s.notes && s.notes.trim()) {
      sectionLabel("My Notes");
      const noteLines = doc.splitTextToSize(s.notes.trim(), contentW - 8);
      const noteH = noteLines.length * 5 + 10;
      checkY(noteH + 4);
      doc.setFillColor(240, 247, 255);
      doc.roundedRect(margin, y, contentW, noteH, 2, 2, "F");
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
      doc.setTextColor(51, 65, 85);
      doc.text(noteLines, margin + 4, y + 7);
      y += noteH + 6;
    }

    doc.setFillColor(28, 58, 94);
    doc.rect(0, 287, pageW, 10, "F");
    doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
    doc.setTextColor(186, 230, 253);
    doc.text("Estimates only. Verify with each school's financial aid office. Loans must be repaid with interest.", margin, 293);
    doc.setTextColor(240, 192, 96);
    doc.text("© 2026 builtbyd Freelance Studio", pageW - margin, 293, { align: "right" });
  });

  const filename = studentName && studentName.trim()
    ? `college-costs-${studentName.trim().replace(/\s+/g, "-").toLowerCase()}.pdf`
    : "college-cost-comparison.pdf";
  doc.save(filename);
}

export default function App() {
  const [schools, setSchools] = useState(() => {
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return [defaultSchool()];
  });
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(SAVE_KEY);
      if (saved) return JSON.parse(saved)[0]?.id;
    } catch (e) {}
    return schools[0].id;
  });
  const [showComparison, setShowComparison] = useState(false);
  const [showFourYear, setShowFourYear] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [costView, setCostView] = useState("full");
  const [schoolSearch, setSchoolSearch] = useState("");
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [prefillNote, setPrefillNote] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem("beechcroft_onboarded"));
  const [showTerms, setShowTerms] = useState(false);
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [supportForm, setSupportForm] = useState({ name: "", email: "", school: "", role: "", heard: "", amount: "" });
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportDiscordFailed, setSupportDiscordFailed] = useState(false);
  const [showDonation, setShowDonation] = useState(() => !localStorage.getItem("collegecosts_donation_dismissed"));
  const [requestForm, setRequestForm] = useState({ name: "", school: "", email: "", role: "" });
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [onboardStep, setOnboardStep] = useState(0);
  const [saveToast, setSaveToast] = useState(false);
  const [studentName, setStudentName] = useState(() => localStorage.getItem("beechcroft_student_name") || "");
  const [hasSaved, setHasSaved] = useState(() => !!localStorage.getItem(SAVE_KEY));

  const width = useWindowWidth();
  const isMobile = width < 768;

  const activeSchool = schools.find(s => s.id === activeTab) || schools[0];
  const activeIdx = schools.findIndex(s => s.id === activeTab);
  const accentColor = COLORS[activeIdx] || COLORS[0];
  const accentLight = LIGHT_COLORS[activeIdx] || LIGHT_COLORS[0];

  const saveProgress = () => {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(schools));
      if (studentName) localStorage.setItem("beechcroft_student_name", studentName);
      setHasSaved(true);
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2500);
    } catch (e) {}
  };

  const clearProgress = () => {
    if (window.confirm("Clear all saved data and start over?")) {
      localStorage.removeItem(SAVE_KEY);
      localStorage.removeItem("beechcroft_student_name");
      setStudentName("");
      const fresh = [defaultSchool()];
      setSchools(fresh);
      setActiveTab(fresh[0].id);
      setHasSaved(false);
    }
  };

  const dismissOnboarding = () => {
    localStorage.setItem("beechcroft_onboarded", "1");
    setShowOnboarding(false);
    setOnboardStep(0);
  };

  const addSchool = () => {
    if (schools.length >= 7) return;
    const ns = defaultSchool();
    setSchools([...schools, ns]);
    setActiveTab(ns.id);
  };
  const removeSchool = (id) => {
    const updated = schools.filter(s => s.id !== id);
    setSchools(updated);
    if (activeTab === id) setActiveTab(updated[0]?.id || null);
  };
  const updateField = (field, value) =>
    setSchools(schools.map(s => s.id === activeTab ? { ...s, [field]: value } : s));
  const toggleIKnowICan = () =>
    setSchools(schools.map(s => s.id === activeTab ? { ...s, iKnowICan: !s.iKnowICan } : s));
  const addOutsideAidRow = () =>
    setSchools(schools.map(s => s.id === activeTab
      ? { ...s, outsideAid: [...(s.outsideAid || []), { id: Date.now() + Math.random(), label: "", amount: "" }] } : s));
  const removeOutsideAidRow = (aidId) =>
    setSchools(schools.map(s => s.id === activeTab
      ? { ...s, outsideAid: s.outsideAid.filter(a => a.id !== aidId) } : s));
  const updateOutsideAid = (aidId, field, value) =>
    setSchools(schools.map(s => s.id === activeTab
      ? { ...s, outsideAid: s.outsideAid.map(a => a.id === aidId ? { ...a, [field]: value } : a) } : s));

  const stats = calcSchool(activeSchool);

  const ONBOARD_STEPS = [
    { emoji: "👋", title: "Welcome to College Costs", body: "This tool helps you compare real costs from your award letters — so you can find your best financial fit, not just your dream school.", tip: null },
    { emoji: "🏫", title: "Add each school you're considering", body: "Click '+ Add School' to add up to 7 schools. Each gets its own color-coded tab. Start by typing the school name at the top of the form.", tip: "You can remove a school anytime by clicking the × on its tab." },
    { emoji: "📄", title: "Enter numbers from your award letter", body: "Fill in Direct Costs (tuition, room & board, fees) and what the school is offering you in Aid. Find these numbers on the official award letter from each school.", tip: "Grants & scholarships = free money. Loans must be repaid!" },
    { emoji: "🌟", title: "Add outside scholarships too", body: "Won a local scholarship or award? Add it in the Outside Aid section. Every dollar counts toward reducing your out-of-pocket cost.", tip: null },
    { emoji: "📊", title: "Watch your Cost Summary update live", body: "As you type, the Cost Summary panel on the right (or tap 📊 Summary on mobile) updates instantly — showing your real out-of-pocket cost after all aid.", tip: "Out-of-pocket is what you actually need to pay each year." },
    { emoji: "⚖️", title: "Compare all schools side by side", body: "Click 'Compare All' in the header to see every school next to each other. The calculator will highlight the best value automatically.", tip: null },
    { emoji: "📝", title: "Take notes & export to PDF", body: "Use the My Notes section on each school to jot down thoughts, pros/cons, or questions. When you're ready, click 'Export PDF' to save or share your full comparison.", tip: "Your notes are included in the PDF — great for sharing with parents or your counselor!" },
  ];

  const InputField = ({ label, field, helper, emoji }) => (
    <div style={{ marginBottom: "14px" }}>
      <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px" }}>
        {emoji && <span style={{ marginRight: "5px" }}>{emoji}</span>}{label}
        {helper && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: "5px", color: "#94a3b8", fontSize: "10px" }}>{helper}</span>}
      </label>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontFamily: "monospace", fontSize: "13px" }}>$</span>
        <input type="number" min="0" placeholder="0"
          key={activeSchool.id + field}
          defaultValue={activeSchool[field]}
          className="glass-input" style={{ width: "100%", boxSizing: "border-box", borderRadius: "12px", padding: "12px 12px 12px 26px", color: "#1e293b", fontFamily: "monospace", fontSize: "16px", outline: "none" }}
          onFocus={e => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}20`; }}
          onBlur={e => { updateField(field, e.target.value); e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
          onDoubleClick={e => e.target.select()}
        />
      </div>
    </div>
  );

  const SectionCard = ({ icon, title, color, lightColor, children }) => (
    <div className="glass-card" style={{ borderRadius: "20px", padding: "20px", marginBottom: "16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
        <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: lightColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>{icon}</div>
        <span style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color }}>{title}</span>
      </div>
      {children}
    </div>
  );

  const SummaryPanel = () => (
    <div className="glass-summary" style={{ borderRadius: "24px", padding: "22px", overflow: "hidden", position: "relative" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: `linear-gradient(90deg,${accentColor},#F0C060)` }} />
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
        <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: accentColor }} />
        <span style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94a3b8" }}>Cost Summary</span>
      </div>
      <SumRow label="Direct Costs" value={fmt(stats.directCosts)} />
      <SumRow label="Indirect Costs" value={fmt(stats.indirectCosts)} />
      <div style={{ margin: "10px 0", height: "1px", background: "#f1f5f9" }} />
      <SumRow label="Total Cost of Attendance" value={fmt(stats.totalCOA)} bold />

      <div style={{ margin: "12px 0 8px", fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#10B981" }}>✅ Free Money (no repayment)</div>
      <SumRow label="Grants & Scholarships" value={`- ${fmt(stats.freeAid)}`} color="#10B981" />
      <SumRow label="Work-Study (earned)" value={`- ${fmt(stats.workStudyAid)}`} color="#3B82F6" />

      <div style={{ margin: "12px 0 8px", fontSize: "11px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#EF4444" }}>⚠️ Loans (must be repaid)</div>
      <SumRow label="Subsidized & Unsubsidized" value={`- ${fmt(stats.loans)}`} color="#EF4444" />
      {stats.loanFees > 0 && <SumRow label="Loan Fees" value={fmt(stats.loanFees)} color="#EF4444" />}

      <div style={{ margin: "10px 0", height: "1px", background: "#f1f5f9" }} />

      {stats.refund > 0 ? (
        <div style={{ background: "#ECFDF5", borderRadius: "10px", padding: "10px 12px", border: "1px solid #10B98125", marginBottom: "4px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
            <span style={{ fontSize: "12px", fontWeight: 700, color: "#065F46" }}>Net Cost After All Aid</span>
            <span style={{ background: "#10B981", color: "#fff", fontSize: "10px", fontWeight: 800, padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap" }}>💰 REFUND</span>
          </div>
          <div style={{ textAlign: "right", fontFamily: "'JetBrains Mono',monospace", fontSize: "20px", fontWeight: 900, color: "#10B981" }}>
            - {fmt(stats.refund)}
          </div>
          <p style={{ margin: "4px 0 0", fontSize: "10px", color: "#6B7280", lineHeight: 1.4 }}>Aid exceeds your total cost — you may receive a refund check. Note: any refund from loan funds must be repaid.</p>
        </div>
      ) : (
        <div style={{ marginBottom: "4px" }}>
          <SumRow label="Net Cost After All Aid" value={fmt(Math.max(0, stats.totalCOA - stats.freeAid - stats.workStudyAid - stats.loans))} bold color="#F59E0B" />
          {stats.loans > 0 && (
            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px", paddingLeft: "4px" }}>
              ↳ includes <span style={{ color: "#EF4444", fontWeight: 600 }}>{fmt(stats.loans)} in loans</span> — must be repaid
            </div>
          )}
        </div>
      )}

      {/* ── Cost View Toggle ── */}
      <div style={{ marginTop: "18px" }}>
        <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "10px" }}>📌 What You'll Pay / Year</div>
        <div style={{ display: "flex", background: "#F1F5F9", borderRadius: "10px", padding: "3px", marginBottom: "12px" }}>
          <button
            onClick={() => setCostView("full")}
            style={{ flex: 1, padding: "7px 8px", borderRadius: "8px", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 700, transition: "all 0.2s",
              background: costView === "full" ? accentColor : "transparent",
              color: costView === "full" ? "#fff" : "#64748b" }}>
            🎒 Full Cost
          </button>
          <button
            onClick={() => setCostView("school")}
            style={{ flex: 1, padding: "7px 8px", borderRadius: "8px", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 700, transition: "all 0.2s",
              background: costView === "school" ? "#1C3A5E" : "transparent",
              color: costView === "school" ? "#fff" : "#64748b" }}>
            🏫 School Bill
          </button>
        </div>

        {costView === "school" ? (
          <div style={{ background: "#EEF4FB", borderRadius: "12px", padding: "16px", border: "1.5px solid #1C3A5E20" }}>
            <div style={{ fontSize: "10px", fontWeight: 800, color: "#1C3A5E", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>School Bill Only</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "8px" }}>
              <div style={{ fontSize: "10px", color: "#64748b", lineHeight: 1.6, maxWidth: "130px" }}>Tuition, room & board, and fees — minus all aid including loans. This is what you'll actually owe the school.</div>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "28px", fontWeight: 900, color: "#1C3A5E" }}>{fmt(Math.max(0, stats.netDirectCost - stats.loans - stats.loanFees))}</span>
            </div>
            {stats.loans > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", padding: "6px 8px", background: "#FEF2F2", borderRadius: "6px" }}>
                <span style={{ fontSize: "10px", color: "#EF4444", fontWeight: 600 }}>⚠️ Loans applied: {fmt(stats.loans)}{stats.loanFees > 0 ? ` + ${fmt(stats.loanFees)} fees` : ""} (must be repaid)</span>
              </div>
            )}
            <div style={{ height: "1px", background: "#1C3A5E15", margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "10px", fontWeight: 700, color: "#1C3A5E", opacity: 0.6, textTransform: "uppercase", letterSpacing: "0.04em" }}>4-Year School Bills</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "16px", fontWeight: 800, color: "#1C3A5E", opacity: 0.8 }}>{fmt(Math.max(0, stats.netDirectCost - stats.loans - stats.loanFees) * 4)}</span>
            </div>
            <div style={{ marginTop: "10px", padding: "8px 10px", background: "#fff", borderRadius: "8px", border: "1px solid #1C3A5E15" }}>
              <p style={{ margin: 0, fontSize: "10px", color: "#64748b", lineHeight: 1.6 }}>
                💡 Does <strong style={{ color: "#1C3A5E" }}>not</strong> include living expenses. Switch to Full Cost to see your complete budget.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ background: accentLight, borderRadius: "12px", padding: "16px", border: `1.5px solid ${accentColor}25` }}>
            <div style={{ fontSize: "10px", fontWeight: 800, color: accentColor, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>Full Cost / Year</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "8px" }}>
              <div style={{ fontSize: "10px", color: "#64748b", lineHeight: 1.6, maxWidth: "130px" }}>School bill + books, transportation, and personal costs. Budget for this even though you won't receive one bill for it.</div>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "28px", fontWeight: 900, color: accentColor }}>{fmt(stats.outOfPocket)}</span>
            </div>
            {stats.loans > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", padding: "6px 8px", background: "#FEF2F2", borderRadius: "6px" }}>
                <span style={{ fontSize: "10px", color: "#EF4444", fontWeight: 600 }}>⚠️ Loans applied: {fmt(stats.loans)}{stats.loanFees > 0 ? ` + ${fmt(stats.loanFees)} fees` : ""} (must be repaid)</span>
              </div>
            )}
            <div style={{ height: "1px", background: `${accentColor}20`, margin: "8px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "10px", fontWeight: 700, color: accentColor, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.04em" }}>4-Year Total</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "16px", fontWeight: 800, color: accentColor, opacity: 0.85 }}>{fmt(stats.fourYearOOP)}</span>
            </div>
            <div style={{ marginTop: "10px", padding: "8px 10px", background: "rgba(255,255,255,0.6)", borderRadius: "8px", border: `1px solid ${accentColor}15` }}>
              <p style={{ margin: 0, fontSize: "10px", color: "#64748b", lineHeight: 1.6 }}>
                💡 Includes <strong style={{ color: accentColor }}>{fmt(stats.indirectCosts)}</strong> in estimated living expenses. Switch to School Bill to see just what the school charges.
              </p>
            </div>
          </div>
        )}
      </div>

      {stats.refund > 0 && (
        <div style={{ marginTop: "12px", background: "#ECFDF5", borderRadius: "12px", padding: "16px", border: "1.5px solid #10B98130" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
            <span style={{ fontSize: "16px" }}>💰</span>
            <div style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#10B981" }}>Est. Refund / Year</div>
          </div>
          <div style={{ fontSize: "28px", fontWeight: 900, color: "#10B981", fontFamily: "'JetBrains Mono',monospace", letterSpacing: "-0.02em" }}>{fmt(stats.refund)}</div>
          <div style={{ height: "1px", background: "#10B98120", margin: "10px 0" }} />
          <div style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#10B981", opacity: 0.7, marginBottom: "4px" }}>4-Year Refund</div>
          <div style={{ fontSize: "18px", fontWeight: 800, color: "#10B981", fontFamily: "'JetBrains Mono',monospace", opacity: 0.85 }}>{fmt(stats.fourYearRefund)}</div>
          <div style={{ marginTop: "8px", fontSize: "11px", color: "#065F46", lineHeight: 1.6 }}>
            ⚠️ Refunds from <strong>loan funds must be repaid</strong> with interest.
          </div>
        </div>
      )}
      <div style={{ marginTop: "12px", fontSize: "11px", color: "#94a3b8", lineHeight: 1.6 }}>
        💡 <strong style={{ color: "#EF4444" }}>Loans are not free money</strong> — they must be repaid with interest after graduation.
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #EEF4FB 0%, #F0F7FF 30%, #FAFCFF 60%, #f0f7ff 100%)", fontFamily: "'Inter', sans-serif", color: "#1e293b", position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Playfair+Display:wght@400;500;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type=number] { -moz-appearance: textfield; }
        ::-webkit-scrollbar { height: 4px; width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(28,58,94,0.2); border-radius: 4px; }
        .toggle-btn:hover { background: rgba(255,255,255,0.6) !important; }
        .add-aid-btn:hover { background: rgba(240,192,96,0.15) !important; border-color: #1C3A5E !important; color: #1C3A5E !important; }
        .remove-aid-btn:hover { background: rgba(239,68,68,0.1) !important; color: #EF4444 !important; }
        .save-btn:hover { background: #0D2137 !important; }
        textarea:focus { outline: none; }
        .glass-card {
          background: rgba(255,255,255,0.55) !important;
          backdrop-filter: blur(20px) saturate(180%) !important;
          -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
          border: 1px solid rgba(28,58,94,0.12) !important;
          box-shadow: 0 4px 24px rgba(28,58,94,0.07), inset 0 1px 0 rgba(255,255,255,0.9) !important;
          overflow: visible !important;
        }
        .glass-input {
          background: rgba(255,255,255,0.6) !important;
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
          border: 1px solid rgba(255,255,255,0.8) !important;
          transition: all 0.2s ease !important;
        }
        .glass-input:focus { 
          background: rgba(255,255,255,0.9) !important;
          box-shadow: 0 0 0 3px rgba(28,58,94,0.12) !important;
        }
        .glass-header {
          background: rgba(255,255,255,0.72) !important;
          backdrop-filter: blur(28px) saturate(200%) !important;
          -webkit-backdrop-filter: blur(28px) saturate(200%) !important;
          border-bottom: 1px solid rgba(255,255,255,0.6) !important;
        }
        .glass-tab {
          backdrop-filter: blur(12px) !important;
          -webkit-backdrop-filter: blur(12px) !important;
          transition: all 0.2s ease !important;
        }
        .glass-tab:hover { background: rgba(255,255,255,0.7) !important; }
        .glass-summary {
          background: rgba(255,255,255,0.5) !important;
          backdrop-filter: blur(24px) saturate(180%) !important;
          -webkit-backdrop-filter: blur(24px) saturate(180%) !important;
          border: 1px solid rgba(28,58,94,0.12) !important;
          box-shadow: 0 8px 32px rgba(28,58,94,0.08), inset 0 1px 0 rgba(255,255,255,0.95) !important;
        }
        @media print {
          @page { size: landscape; margin: 12mm; }
          html, body { font-size: 9px !important; background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .glass-card { background: #fff !important; backdrop-filter: none !important; -webkit-backdrop-filter: none !important; border: 1px solid #e2e8f0 !important; box-shadow: none !important; break-inside: avoid; padding: 10px !important; border-radius: 8px !important; }
          .print-comparison-grid { display: grid !important; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)) !important; gap: 8px !important; }
          .print-comparison-grid * { font-size: 8.5px !important; line-height: 1.3 !important; }
          .print-header { display: flex !important; align-items: center !important; gap: 12px !important; margin-bottom: 10px !important; padding-bottom: 8px !important; border-bottom: 2px solid #1C3A5E !important; }
        }
      `}</style>

      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "-15%", left: "-10%", width: "55vw", height: "55vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(240,192,96,0.18) 0%, transparent 70%)", filter: "blur(40px)" }} />
        <div style={{ position: "absolute", top: "30%", right: "-15%", width: "50vw", height: "50vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(28,58,94,0.1) 0%, transparent 70%)", filter: "blur(50px)" }} />
        <div style={{ position: "absolute", bottom: "-10%", left: "20%", width: "60vw", height: "40vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)", filter: "blur(60px)" }} />
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        {saveToast && (
          <div className="no-print" style={{ position: "fixed", top: "20px", left: "50%", transform: "translateX(-50%)", background: "#10B981", color: "#fff", padding: "10px 22px", borderRadius: "12px", fontWeight: 700, fontSize: "14px", zIndex: 9999, boxShadow: "0 4px 16px rgba(16,185,129,0.3)", display: "flex", alignItems: "center", gap: "8px" }}>
            ✅ Progress saved!
          </div>
        )}

        {/* Header */}
        <div className="glass-header no-print" style={{ padding: isMobile ? "14px 16px" : "20px 28px", position: "sticky", top: 0, zIndex: 100 }}>
          <div style={{ maxWidth: "1120px", margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <img src="/builtbyd-logo.svg" alt="builtbyd" style={{ height: isMobile ? "52px" : "72px", width: "auto", objectFit: "contain" }} />
                <div style={{ borderLeft: "2px solid #e2e8f0", paddingLeft: "16px" }}>
                  <h1 style={{ margin: 0, fontSize: isMobile ? "20px" : "28px", fontWeight: 900, color: "#1C3A5E", fontFamily: "'Playfair Display', serif", lineHeight: 1.2 }}>
                    College <span style={{ color: "#F0C060", WebkitTextStroke: "0.5px #1C3A5E" }}>Costs</span>
                  </h1>
                  {!isMobile && <p style={{ margin: "4px 0 0", fontSize: "13px", color: "#94a3b8", fontWeight: 500 }}>Know what you'll pay. Know what it's worth.</p>}
                </div>
              </div>
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                <button className="glass-tab" onClick={() => { setShowFourYear(!showFourYear); setShowComparison(false); setShowSummary(false); }}
                  style={{ padding: isMobile ? "6px 10px" : "8px 16px", borderRadius: "12px", border: showFourYear ? "1px solid rgba(28,58,94,0.3)" : "1px solid rgba(255,255,255,0.6)", background: showFourYear ? "rgba(28,58,94,0.12)" : "rgba(255,255,255,0.5)", color: showFourYear ? "#1C3A5E" : "#64748b", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                  📅 {isMobile ? "4-Yr" : "4-Year View"}
                </button>
                <button className="glass-tab" onClick={() => { setShowComparison(!showComparison); setShowFourYear(false); setShowSummary(false); }}
                  style={{ padding: isMobile ? "6px 10px" : "8px 16px", borderRadius: "12px", border: showComparison ? "1px solid rgba(28,58,94,0.3)" : "1px solid rgba(255,255,255,0.6)", background: showComparison ? "rgba(28,58,94,0.12)" : "rgba(255,255,255,0.5)", color: showComparison ? "#1C3A5E" : "#64748b", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                  ⚖️ {isMobile ? "Compare" : "Compare All"}
                </button>
                {isMobile && (
                  <button className="glass-tab" onClick={() => { setShowSummary(!showSummary); setShowComparison(false); setShowFourYear(false); }}
                    style={{ padding: "6px 10px", borderRadius: "12px", border: showSummary ? "1px solid rgba(28,58,94,0.3)" : "1px solid rgba(255,255,255,0.6)", background: showSummary ? "rgba(28,58,94,0.12)" : "rgba(255,255,255,0.5)", color: showSummary ? "#1C3A5E" : "#64748b", fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                    📊 Summary
                  </button>
                )}
                <button onClick={() => exportPDF(schools, studentName)}
                  style={{ padding: isMobile ? "6px 10px" : "8px 16px", borderRadius: "10px", border: "1.5px solid #1C3A5E", background: "#1C3A5E", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                  📄 {isMobile ? "PDF" : "Export PDF"}
                </button>
                <button className="save-btn" onClick={saveProgress}
                  style={{ padding: isMobile ? "6px 10px" : "8px 16px", borderRadius: "10px", border: "1.5px solid #1C3A5E", background: "#1C3A5E", color: "#F0C060", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                  💾 {isMobile ? "Save" : "Save Progress"}
                </button>
                {hasSaved && (
                  <button onClick={clearProgress}
                    style={{ padding: isMobile ? "6px 8px" : "8px 12px", borderRadius: "10px", border: "1.5px solid #e2e8f0", background: "#fff", color: "#94a3b8", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "10px" : "12px", fontWeight: 600, cursor: "pointer" }}>
                    🗑️ {isMobile ? "" : "Clear"}
                  </button>
                )}
              </div>
            </div>

            {hasSaved && !saveToast && (
              <div style={{ marginBottom: "10px", background: "#F0F7FF", border: "1.5px solid #F0C06050", borderRadius: "8px", padding: "6px 12px", fontSize: "11px", color: "#1C3A5E", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                ✨ Your progress is saved — it will be here when you come back!
              </div>
            )}

            <div style={{ marginBottom: "14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "rgba(255,255,255,0.7)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", borderRadius: "14px", border: "1px solid rgba(28,58,94,0.1)", padding: "10px 16px", boxShadow: "0 2px 8px rgba(28,58,94,0.05)" }}>
                <span style={{ fontSize: "16px", flexShrink: 0 }}>🎓</span>
                <input type="text" placeholder="Enter your name..." defaultValue={studentName}
                  onBlur={e => { setStudentName(e.target.value); localStorage.setItem("beechcroft_student_name", e.target.value); }}
                  style={{ flex: 1, border: "none", background: "transparent", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1C3A5E", outline: "none" }}
                />
                {studentName && <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: 500, whiteSpace: "nowrap" }}>shows on PDF</span>}
              </div>
            </div>

            <div style={{ display: "flex", gap: "6px", overflowX: "auto", paddingBottom: "4px" }}>
              {schools.map((s, i) => (
                <button key={s.id} onClick={() => { setActiveTab(s.id); setShowComparison(false); setShowFourYear(false); setShowSummary(false); }}
                  style={{ display: "flex", alignItems: "center", gap: "6px", padding: isMobile ? "6px 10px" : "7px 14px", borderRadius: "10px", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, whiteSpace: "nowrap", transition: "all 0.15s", border: activeTab === s.id ? `1.5px solid ${COLORS[i]}` : "1.5px solid #f1f5f9", background: activeTab === s.id ? LIGHT_COLORS[i] : "#f8fafc", color: activeTab === s.id ? COLORS[i] : "#94a3b8", boxShadow: activeTab === s.id ? `0 2px 8px ${COLORS[i]}20` : "none" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: COLORS[i], flexShrink: 0 }} />
                  {named(s)}
                  {schools.length > 1 && (
                    <span onClick={e => { e.stopPropagation(); removeSchool(s.id); }} style={{ marginLeft: "2px", color: "#cbd5e1", fontSize: "16px", lineHeight: 1, cursor: "pointer", fontWeight: 400 }}>×</span>
                  )}
                </button>
              ))}
              {schools.length < 7 && (
                <button onClick={addSchool} style={{ padding: isMobile ? "6px 10px" : "7px 14px", borderRadius: "10px", border: "1.5px dashed #cbd5e1", background: "transparent", color: "#94a3b8", fontFamily: "'Inter', sans-serif", fontSize: isMobile ? "11px" : "13px", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  + Add School
                </button>
              )}
            </div>
          </div>
        </div>

        <div style={{ maxWidth: "1120px", margin: "0 auto", padding: isMobile ? "16px" : "24px 28px" }}>

          {/* COMPARISON VIEW */}
          {showComparison && (
            <div className="print-area">
              <div className="print-header" style={{ display: "none" }}>
                <img src="/builtbyd-logo.svg" alt="builtbyd" style={{ height: "48px", width: "auto", mixBlendMode: "multiply" }} />
                <div>
                  <div style={{ fontSize: "18px", fontWeight: 900, color: "#1C3A5E", fontFamily: "'Playfair Display', serif" }}>College Costs</div>
                  <div style={{ fontSize: "11px", color: "#94a3b8" }}>Know what you'll pay. Know what it's worth. — {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
                </div>
              </div>
              <div className="no-print" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <h2 style={{ margin: 0, fontSize: "13px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8" }}>Side-by-Side Comparison</h2>
                <button onClick={() => window.print()}
                  style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 18px", borderRadius: "12px", border: "1.5px solid #1C3A5E", background: "#1C3A5E", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                  🖨️ Print
                </button>
              </div>
              <div className="print-comparison-grid" style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${Math.min(schools.length, 3)}, 1fr)`, gap: "16px" }}>
                {schools.map((s, i) => {
                  const c = calcSchool(s);
                  return (
                    <div key={s.id} className="glass-card" style={{ borderRadius: "22px", padding: "22px", position: "relative", overflow: "hidden", border: `1px solid rgba(255,255,255,0.75)` }}>
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: COLORS[i], borderRadius: "16px 16px 0 0" }} />
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                        <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: COLORS[i] }} />
                        <span style={{ fontWeight: 900, fontSize: "15px" }}>{named(s)}</span>
                      </div>
                      <CRow label="Total Cost of Attendance" value={fmt(c.totalCOA)} bold />
                      <div style={{ height: "1px", background: "#f1f5f9", margin: "10px 0" }} />
                      <CRow label="Direct Costs" value={fmt(c.directCosts)} muted />
                      <CRow label="Indirect Costs" value={fmt(c.indirectCosts)} muted />
                      <div style={{ height: "1px", background: "#f1f5f9", margin: "10px 0" }} />
                      <CRow label="Free Aid" value={`- ${fmt(c.freeAid)}`} color="#10B981" />
                      <CRow label="Work-Study" value={`- ${fmt(c.workStudyAid)}`} color="#3B82F6" />
                      <div style={{ height: "1px", background: "#f1f5f9", margin: "10px 0" }} />
                      <CRow label="Net Cost After All Aid" value={fmt(c.netCost)} bold color="#F59E0B" />
                      <CRow label="Loans (repaid later)" value={fmt(c.loans)} color="#EF4444" />

                      {/* Toggle per card */}
                      <div style={{ marginTop: "16px" }}>
                        <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px" }}>What You'll Pay / Year</div>
                        <div style={{ display: "flex", background: "#F1F5F9", borderRadius: "8px", padding: "3px", marginBottom: "10px" }}>
                          <button onClick={() => setCostView("full")}
                            style={{ flex: 1, padding: "5px 6px", borderRadius: "6px", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 700, transition: "all 0.2s",
                              background: costView === "full" ? COLORS[i] : "transparent",
                              color: costView === "full" ? "#fff" : "#64748b" }}>
                            🎒 Full Cost
                          </button>
                          <button onClick={() => setCostView("school")}
                            style={{ flex: 1, padding: "5px 6px", borderRadius: "6px", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", fontSize: "10px", fontWeight: 700, transition: "all 0.2s",
                              background: costView === "school" ? "#1C3A5E" : "transparent",
                              color: costView === "school" ? "#fff" : "#64748b" }}>
                            🏫 School Bill
                          </button>
                        </div>
                        {costView === "school" ? (
                          <div style={{ background: "#EEF4FB", borderRadius: "10px", padding: "12px", border: "1.5px solid #1C3A5E20" }}>
                            <div style={{ fontSize: "10px", fontWeight: 800, color: "#1C3A5E", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "3px" }}>School Bill Only</div>
                            <div style={{ fontSize: "24px", fontWeight: 900, color: "#1C3A5E", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(Math.max(0, c.netDirectCost - c.loans - c.loanFees))}</div>
                            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px" }}>After all aid + loans applied to direct costs</div>
                            {(c.loans > 0 || c.loanFees > 0) && <div style={{ fontSize: "10px", color: "#EF4444", marginTop: "3px", fontWeight: 600 }}>⚠️ Includes {fmt(c.loans)}{c.loanFees > 0 ? ` + ${fmt(c.loanFees)} fees` : ""} in loans</div>}
                            <div style={{ height: "1px", background: "#1C3A5E15", margin: "8px 0" }} />
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 600 }}>4-Year</span>
                              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", fontWeight: 700, color: "#1C3A5E", opacity: 0.75 }}>{fmt(Math.max(0, c.netDirectCost - c.loans - c.loanFees) * 4)}</span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ background: LIGHT_COLORS[i], borderRadius: "10px", padding: "12px", border: `1.5px solid ${COLORS[i]}25` }}>
                            <div style={{ fontSize: "10px", fontWeight: 800, color: COLORS[i], textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "3px" }}>Full Cost / Year</div>
                            <div style={{ fontSize: "24px", fontWeight: 900, color: COLORS[i], fontFamily: "'JetBrains Mono',monospace" }}>{fmt(c.outOfPocket)}</div>
                            <div style={{ fontSize: "10px", color: "#64748b", marginTop: "3px" }}>Includes living expenses you manage yourself</div>
                            {c.loans > 0 && <div style={{ fontSize: "10px", color: "#EF4444", marginTop: "3px", fontWeight: 600 }}>⚠️ Loans applied: {fmt(c.loans)} (must be repaid)</div>}
                            <div style={{ height: "1px", background: `${COLORS[i]}20`, margin: "8px 0" }} />
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span style={{ fontSize: "10px", color: "#94a3b8", fontWeight: 600 }}>4-Year</span>
                              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", fontWeight: 700, color: COLORS[i], opacity: 0.85 }}>{fmt(c.fourYearOOP)}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {c.refund > 0 && (
                        <div style={{ marginTop: "10px", background: "#ECFDF5", borderRadius: "12px", padding: "12px", border: "1.5px solid #10B98130" }}>
                          <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#10B981", marginBottom: "4px" }}>💰 Est. Refund / Year</div>
                          <div style={{ fontSize: "22px", fontWeight: 900, color: "#10B981", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(c.refund)}</div>
                          <div style={{ fontSize: "10px", color: "#065F46", marginTop: "4px" }}>⚠️ Loan refunds must be repaid</div>
                        </div>
                      )}
                      {s.notes && s.notes.trim() && (
                        <div style={{ marginTop: "12px", background: "#F0F7FF", borderRadius: "10px", padding: "10px 12px", border: "1.5px solid #F0C06040" }}>
                          <div style={{ fontSize: "10px", fontWeight: 800, color: "#1C3A5E", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px" }}>📝 My Notes</div>
                          <div style={{ fontSize: "12px", color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{s.notes}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {schools.filter(s => s.name).length > 1 && (() => {
                const best = [...schools].map(s => ({ s, c: calcSchool(s) })).sort((a, b) => a.c.outOfPocket - b.c.outOfPocket)[0];
                return (
                  <div style={{ marginTop: "18px", background: "#ECFDF5", border: "1.5px solid #10B98130", borderRadius: "12px", padding: "14px 18px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "22px" }}>🏆</span>
                    <span style={{ fontWeight: 700, color: "#065F46" }}>{named(best.s)}</span>
                    <span style={{ color: "#6B7280", fontSize: "13px" }}>has the lowest estimated out-of-pocket at</span>
                    <span style={{ fontWeight: 800, color: "#10B981", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(best.c.outOfPocket)}/yr</span>
                  </div>
                );
              })()}
              <Disclaimer />
            </div>
          )}

          {/* 4-YEAR VIEW */}
          {showFourYear && !showComparison && (
            <div>
              <h2 style={{ fontSize: "13px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "20px" }}>4-Year Projection</h2>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${Math.min(schools.length, 3)}, 1fr)`, gap: "16px" }}>
                {schools.map((s, i) => {
                  const c = calcSchool(s);
                  return (
                    <div key={s.id} className="glass-card" style={{ borderRadius: "22px", padding: "22px", position: "relative", overflow: "hidden", border: `1px solid rgba(255,255,255,0.75)` }}>
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "4px", background: COLORS[i] }} />
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                        <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: COLORS[i] }} />
                        <span style={{ fontWeight: 900, fontSize: "15px" }}>{named(s)}</span>
                      </div>
                      <FourYearStat label="Total 4-Year COA" value={fmt(c.fourYearCOA)} color="#1e293b" />
                      <FourYearStat label="Total Free Aid" value={`- ${fmt(c.fourYearFreeAid)}`} color="#10B981" />
                      <FourYearStat label="Total Loan Debt" value={fmt(c.totalLoanDebt)} color="#EF4444" />
                      {c.fourYearLoanFees > 0 && (
                        <div style={{ marginTop: "-8px", marginBottom: "8px", paddingLeft: "8px", borderLeft: "2px solid #EF444430" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#94a3b8" }}>
                            <span>↳ Loans ({fmt(c.fourYearLoans)}) + Fees ({fmt(c.fourYearLoanFees)})</span>
                          </div>
                        </div>
                      )}
                      <div style={{ height: "1px", background: "#f1f5f9", margin: "12px 0" }} />
                      <div style={{ background: LIGHT_COLORS[i], borderRadius: "12px", padding: "14px", border: `1.5px solid ${COLORS[i]}25`, marginBottom: "12px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: COLORS[i], marginBottom: "4px" }}>4-Year Out-of-Pocket</div>
                        <div style={{ fontSize: "26px", fontWeight: 900, color: COLORS[i], fontFamily: "'JetBrains Mono',monospace" }}>{fmt(c.fourYearOOP)}</div>
                      </div>
                      {c.monthlyPayment > 0 && (
                        <div style={{ background: "#FEF2F2", borderRadius: "12px", padding: "14px", border: "1.5px solid #EF444425" }}>
                          <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: "#EF4444", marginBottom: "4px" }}>Est. Monthly Loan Payment*</div>
                          <div style={{ fontSize: "22px", fontWeight: 900, color: "#EF4444", fontFamily: "'JetBrains Mono',monospace" }}>{fmt(c.monthlyPayment)}/mo</div>
                          <div style={{ fontSize: "10px", color: "#94a3b8", marginTop: "4px" }}>10-year standard repayment plan</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: "14px", background: "#F0F7FF", border: "1.5px solid #F59E0B30", borderRadius: "10px", padding: "12px 16px" }}>
                <p style={{ margin: 0, fontSize: "12px", color: "#92400E", lineHeight: 1.6 }}>*Loan repayment estimate uses ~6.53% federal interest rate on a 10-year standard plan. Total loan debt includes any loan fees entered. Actual rates and repayment terms will vary.</p>
              </div>
              <Disclaimer />
            </div>
          )}

          {isMobile && showSummary && !showComparison && !showFourYear && (
            <SummaryPanel />
          )}

          {!showComparison && !showFourYear && !(isMobile && showSummary) && activeSchool && (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 300px", gap: "20px", alignItems: "start", overflow: "visible" }}>
              <div style={{ overflow: "visible" }}>
                <div className="glass-card" style={{ borderRadius: "20px", padding: "20px", marginBottom: "16px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: "6px" }}>🏫 School Name</label>
                  <input type="text"
                    placeholder="Type to search Ohio schools, or enter any name..."
                    className="glass-input"
                    style={{ width: "100%", borderRadius: "12px", padding: "12px 14px", color: "#0f172a", fontFamily: "'Inter', sans-serif", fontSize: "15px", fontWeight: 700, outline: "none" }}
                    value={activeSchool.name || ""}
                    onFocus={e => {
                      e.target.style.borderColor = accentColor;
                      e.target.style.background = "#fff";
                      setSchoolSearch(e.target.value);
                      setShowSchoolDropdown(true);
                      const r = e.target.getBoundingClientRect();
                      setDropdownRect({ top: r.bottom + window.scrollY, left: r.left, width: r.width });
                    }}
                    onBlur={e => {
                      setTimeout(() => setShowSchoolDropdown(false), 150);
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.background = "#f8fafc";
                    }}
                    onChange={e => {
                      updateField("name", e.target.value);
                      setSchoolSearch(e.target.value);
                      setShowSchoolDropdown(true);
                      const r = e.target.getBoundingClientRect();
                      setDropdownRect({ top: r.bottom + window.scrollY, left: r.left, width: r.width });
                    }}
                  />
                  {prefillNote && (
                    <div style={{ marginTop: "10px", padding: "9px 12px", background: prefillNote === "2026-27" ? "#ECFDF5" : "#FDF8EE", borderRadius: "8px", border: `1px solid ${prefillNote === "2026-27" ? "#0F6E5620" : "#B8904A20"}`, fontSize: "11px", color: prefillNote === "2026-27" ? "#0F6E56" : "#B8904A", lineHeight: 1.5 }}>
                      ✅ Costs pre-filled from <strong style={{ margin: "0 2px" }}>{prefillNote}</strong> published COA data (on-campus budget). Review each number and update anything that differs from your actual award letter.
                      {prefillNote === "2025-26" && <span style={{ display: "block", marginTop: "3px", fontStyle: "italic" }}> 2026-27 data not yet available for this school.</span>}
                    </div>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
                  <div>
                    <SectionCard icon="🏛️" title="Direct Costs" color="#EF4444" lightColor="#FEF2F2">
                      <InputField label="Tuition" field="tuition" emoji="📚" />
                      <InputField label="Room & Board" field="roomBoard" emoji="🛏️" helper="(on-campus)" />
                      <InputField label="Additional Fees" field="fees" emoji="📋" helper="(activity, tech, etc.)" />
                      <div style={{ marginTop: "8px", paddingTop: "12px", borderTop: "1.5px dashed #EF444440" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "11px", fontWeight: 800, color: "#EF4444", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Direct Costs</span>
                          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "14px", fontWeight: 800, color: "#EF4444" }}>
                            {fmt(num(activeSchool.tuition) + num(activeSchool.roomBoard) + num(activeSchool.fees))}
                          </span>
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: "10px", color: "#94a3b8", lineHeight: 1.5 }}>These are costs billed directly by the school each year.</p>
                      </div>
                    </SectionCard>
                    <SectionCard icon="🎒" title="Indirect Costs" color="#F59E0B" lightColor="#F0F7FF">
                      <InputField label="Books & Supplies" field="books" emoji="📖" />
                      <InputField label="Transportation" field="transportation" emoji="🚗" />
                      <InputField label="Personal & Misc." field="personalMisc" emoji="🛍️" />
                      <div style={{ marginTop: "8px", paddingTop: "12px", borderTop: "1.5px dashed #F0C06050" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "11px", fontWeight: 800, color: "#F59E0B", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Indirect Costs</span>
                          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "14px", fontWeight: 800, color: "#F59E0B" }}>
                            {fmt(num(activeSchool.books) + num(activeSchool.transportation) + num(activeSchool.personalMisc))}
                          </span>
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: "10px", color: "#94a3b8", lineHeight: 1.5 }}>Estimated living expenses not billed by the school — budget accordingly.</p>
                      </div>
                    </SectionCard>
                  </div>
                  <div>
                    <SectionCard icon="📄" title="Aid from Award Letter" color="#10B981" lightColor="#ECFDF5">
                      <InputField label="Institutional Grants" field="grants" emoji="🏫" helper="(school-funded)" />
                      <InputField label="Federal Grants" field="scholarships" emoji="🇺🇸" helper="(Pell Grant, etc.)" />
                      <InputField label="State Grant" field="stateGrant" emoji="🏛️" helper="(OCOG or other state aid)" />
                      <InputField label="Work-Study" field="workStudy" emoji="💼" helper="(must be earned)" />
                      <div style={{ marginBottom: "14px" }}>
                        <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px" }}>
                          ⚠️ Subsidized Loan
                          <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: "5px", color: "#94a3b8", fontSize: "10px" }}>Federal standard amount • $3,500</span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontFamily: "monospace", fontSize: "13px" }}>$</span>
                          <input type="number" min="0" placeholder="0"
                            key={activeSchool.id + "subsidizedLoans"}
                            defaultValue={activeSchool.subsidizedLoans}
                            className="glass-input" style={{ width: "100%", boxSizing: "border-box", borderRadius: "12px", padding: "12px 12px 12px 26px", color: "#EF4444", fontFamily: "monospace", fontSize: "16px", outline: "none" }}
                            onFocus={e => { e.target.style.borderColor = "#EF4444"; e.target.style.boxShadow = "0 0 0 3px #EF444420"; }}
                            onBlur={e => { updateField("subsidizedLoans", e.target.value); e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                          />
                        </div>
                      </div>
                      <div style={{ marginBottom: "14px" }}>
                        <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px" }}>
                          ⚠️ Unsubsidized Loan
                          <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, marginLeft: "5px", color: "#94a3b8", fontSize: "10px" }}>Federal standard amount • $2,000</span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontFamily: "monospace", fontSize: "13px" }}>$</span>
                          <input type="number" min="0" placeholder="0"
                            key={activeSchool.id + "unsubsidizedLoans"}
                            defaultValue={activeSchool.unsubsidizedLoans}
                            className="glass-input" style={{ width: "100%", boxSizing: "border-box", borderRadius: "12px", padding: "12px 12px 12px 26px", color: "#EF4444", fontFamily: "monospace", fontSize: "16px", outline: "none" }}
                            onFocus={e => { e.target.style.borderColor = "#EF4444"; e.target.style.boxShadow = "0 0 0 3px #EF444420"; }}
                            onBlur={e => { updateField("unsubsidizedLoans", e.target.value); e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; }}
                          />
                        </div>
                      </div>
                      <InputField label="Loan Fees" field="loanFees" emoji="💸" helper="(origination fees, etc.)" />
                      <div style={{ marginTop: "8px", paddingTop: "12px", borderTop: "1.5px dashed #10B98140" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 700, color: "#10B981" }}>✅ Free Aid (grants + work-study)</span>
                          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "13px", fontWeight: 800, color: "#10B981" }}>
                            {fmt(num(activeSchool.grants) + num(activeSchool.scholarships) + num(activeSchool.stateGrant) + num(activeSchool.workStudy))}
                          </span>
                        </div>
                        <div style={{ background: "#ECFDF5", borderRadius: "8px", padding: "10px 12px", border: "1.5px solid #10B98125" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "11px", fontWeight: 800, color: "#065F46", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Aid Package</span>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "15px", fontWeight: 900, color: "#065F46" }}>
                              {fmt(num(activeSchool.grants) + num(activeSchool.scholarships) + num(activeSchool.stateGrant) + num(activeSchool.workStudy) + num(activeSchool.subsidizedLoans) + num(activeSchool.unsubsidizedLoans))}
                            </span>
                          </div>
                          <p style={{ margin: "4px 0 0", fontSize: "10px", color: "#6B7280", lineHeight: 1.5 }}>
                            ⚠️ Includes loans — only <strong>{fmt(num(activeSchool.grants) + num(activeSchool.scholarships) + num(activeSchool.stateGrant) + num(activeSchool.workStudy))}</strong> is money you don't have to repay.
                          </p>
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard icon="🌟" title="Outside Aid" color="#1C3A5E" lightColor="#F0F7FF">
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "-8px", marginBottom: "12px" }}>
                        <button className="add-aid-btn" onClick={addOutsideAidRow}
                          style={{ display: "flex", alignItems: "center", gap: "5px", padding: "5px 12px", borderRadius: "8px", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#94a3b8", fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                          <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span> Add
                        </button>
                      </div>
                      <div style={{ marginBottom: "12px", background: activeSchool.iKnowICan ? "#ECFDF5" : "#f8fafc", borderRadius: "10px", padding: "12px", border: activeSchool.iKnowICan ? "1.5px solid #10B98140" : "1.5px solid #f1f5f9", transition: "all 0.2s" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "2px" }}>
                              <span style={{ fontSize: "13px" }}>🎓</span>
                              <span style={{ fontSize: "12px", fontWeight: 800, color: activeSchool.iKnowICan ? "#065F46" : "#374151" }}>I Know I Can Grant</span>
                            </div>
                            <div style={{ fontSize: "11px", color: activeSchool.iKnowICan ? "#10B981" : "#94a3b8", fontWeight: 600 }}>
                              {activeSchool.iKnowICan ? "✅ Applied — $2,500 counted in your aid" : "Columbus-based college access grant • $2,500"}
                            </div>
                          </div>
                          <button onClick={toggleIKnowICan} style={{ width: "44px", height: "24px", borderRadius: "12px", border: "none", cursor: "pointer", position: "relative", transition: "all 0.25s", background: activeSchool.iKnowICan ? "#10B981" : "#cbd5e1", flexShrink: 0 }}>
                            <span style={{ position: "absolute", top: "3px", left: activeSchool.iKnowICan ? "23px" : "3px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff", transition: "left 0.25s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                          </button>
                        </div>
                      </div>
                      {(activeSchool.outsideAid || []).map((aid, idx) => (
                        <div key={aid.id} style={{ marginBottom: "12px", background: "#f8fafc", borderRadius: "10px", padding: "12px", border: "1.5px solid #f1f5f9" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                            <span style={{ fontSize: "11px", fontWeight: 700, color: "#1C3A5E", letterSpacing: "0.04em" }}>🏅 Aid #{idx + 1}</span>
                            {(activeSchool.outsideAid || []).length > 1 && (
                              <button className="remove-aid-btn" onClick={() => removeOutsideAidRow(aid.id)}
                                style={{ width: "22px", height: "22px", borderRadius: "6px", border: "1.5px solid #e2e8f0", background: "#fff", color: "#cbd5e1", fontSize: "14px", lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s", fontWeight: 700 }}>×</button>
                            )}
                          </div>
                          <input type="text" placeholder="e.g. Rotary Club Scholarship"
                            key={activeSchool.id + aid.id + "label"}
                            defaultValue={aid.label}
                            style={{ width: "100%", background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", color: "#1e293b", fontFamily: "'Inter', sans-serif", fontSize: "14px", fontWeight: 600, outline: "none", marginBottom: "8px" }}
                            onFocus={e => { e.target.style.borderColor = "#F0C060"; }}
                            onBlur={e => { updateOutsideAid(aid.id, "label", e.target.value); e.target.style.borderColor = "#e2e8f0"; }}
                          />
                          <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontFamily: "monospace", fontSize: "13px" }}>$</span>
                            <input type="number" min="0" placeholder="0"
                              key={activeSchool.id + aid.id + "amount"}
                              defaultValue={aid.amount}
                              style={{ width: "100%", background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px 10px 26px", color: "#1e293b", fontFamily: "monospace", fontSize: "16px", outline: "none", transition: "border-color 0.2s" }}
                              onFocus={e => { e.target.style.borderColor = "#F0C060"; }}
                              onBlur={e => { updateOutsideAid(aid.id, "amount", e.target.value); e.target.style.borderColor = "#e2e8f0"; }}
                            />
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: "8px", paddingTop: "12px", borderTop: "1.5px dashed #F0C06080" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: "11px", fontWeight: 800, color: "#1C3A5E", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Outside Aid</span>
                          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "14px", fontWeight: 800, color: "#1C3A5E" }}>
                            {fmt((activeSchool.outsideAid || []).reduce((sum, a) => sum + num(a.amount), 0) + (activeSchool.iKnowICan ? 2500 : 0))}
                          </span>
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: "10px", color: "#94a3b8", lineHeight: 1.5 }}>Scholarships and grants from outside the school — all free money.</p>
                      </div>
                    </SectionCard>
                  </div>
                </div>

                <div className="glass-card" style={{ borderRadius: "20px", padding: "20px", marginBottom: "16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                    <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "#F0F7FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>📝</div>
                    <div>
                      <span style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#1C3A5E" }}>My Notes</span>
                      <span style={{ display: "block", fontSize: "10px", color: "#94a3b8", fontWeight: 400, marginTop: "1px" }}>Thoughts, pros/cons, questions — included in PDF export</span>
                    </div>
                  </div>
                  <textarea
                    key={activeSchool.id + "notes"}
                    defaultValue={activeSchool.notes || ""}
                    placeholder={`Jot down your thoughts about ${named(activeSchool)}...\n\nExamples:\n• Campus visit felt great\n• Far from home — extra travel costs?\n• Ask about work-study availability`}
                    rows={5}
                    className="glass-input" style={{ width: "100%", borderRadius: "14px", padding: "12px 14px", color: "#1e293b", fontFamily: "'Inter', sans-serif", fontSize: "13px", lineHeight: 1.6, resize: "vertical", boxSizing: "border-box", border: "1px solid rgba(240,192,96,0.4)", background: "rgba(255,251,230,0.6)" }}
                    onFocus={e => { e.target.style.borderColor = "#F0C060"; }}
                    onBlur={e => { updateField("notes", e.target.value); e.target.style.borderColor = "#F0C06050"; }}
                  />
                </div>

                {isMobile && (
                  <div style={{ marginTop: "8px" }}>
                    <SummaryPanel />
                  </div>
                )}
              </div>

              {!isMobile && (
                <div style={{ position: "sticky", top: "20px" }}>
                  <SummaryPanel />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* School Search Dropdown Portal — fixed position, always on top */}
      {showSchoolDropdown && schoolSearch.length >= 2 && dropdownRect && (() => {
        const matches = OHIO_SCHOOLS.filter(s =>
          s.name.toLowerCase().includes(schoolSearch.toLowerCase())
        ).slice(0, 7);
        if (!matches.length) return null;
        return (
          <div style={{ position: "absolute", top: dropdownRect.top + 4, left: dropdownRect.left, width: dropdownRect.width, zIndex: 9999, background: "#fff", borderRadius: "12px", border: "1.5px solid #e2e8f0", boxShadow: "0 8px 32px rgba(28,58,94,0.18)", overflow: "hidden" }}>
            {matches.map(school => (
              <div key={school.name}
                onMouseDown={() => {
                  setSchools(prev => prev.map(s => s.id === activeTab ? {
                    ...s,
                    name: school.name,
                    tuition: String(school.tuition),
                    roomBoard: String(school.roomBoard),
                    fees: String(school.fees),
                    books: String(school.books),
                    transportation: String(school.transportation),
                    personalMisc: String(school.personalMisc || 0),
                  } : s));
                  setSchoolSearch(school.name);
                  setShowSchoolDropdown(false);
                  setPrefillNote(school.dataYear);
                  setTimeout(() => setPrefillNote(false), 6000);
                }}
                style={{ padding: "11px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "0.5px solid #f1f5f9", background: "#fff" }}
                onMouseEnter={e => e.currentTarget.style.background = "#EEF4FB"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>{school.name}</span>
                <span style={{ fontSize: "10px", fontWeight: 600, padding: "2px 8px", borderRadius: "20px", flexShrink: 0, marginLeft: "8px", background: school.dataYear === "2026-27" ? "#ECFDF5" : "#FDF8EE", color: school.dataYear === "2026-27" ? "#0F6E56" : "#B8904A" }}>{school.dataYear}</span>
              </div>
            ))}
            <div style={{ padding: "8px 16px", fontSize: "10px", color: "#94a3b8", fontStyle: "italic", background: "#f8fafc" }}>
              Type any school name if not listed above
            </div>
          </div>
        );
      })()}

      {/* Donation Banner */}
      {showDonation && (
        <div className="no-print" style={{ background: "linear-gradient(135deg, #0D2137 0%, #1C3A5E 100%)", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "20px" }}>💛</div>
            <div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "13px", color: "#fff" }}>This tool is free — and always will be for students.</div>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: "rgba(255,255,255,0.5)", marginTop: "2px" }}>If it helps your students, a small contribution keeps it running.</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button onClick={() => setShowSupport(true)} style={{ padding: "8px 20px", borderRadius: "10px", border: "none", background: "#F0C060", color: "#0D2137", fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
              Support this tool
            </button>
            <button onClick={() => { setShowDonation(false); localStorage.setItem("collegecosts_donation_dismissed", "true"); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", fontSize: "20px", cursor: "pointer", lineHeight: 1, padding: "4px 6px" }}>×</button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="no-print" style={{ marginTop: "0px", padding: "24px 28px", borderTop: "1px solid rgba(28,58,94,0.1)", textAlign: "center" }}>
        <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8", fontFamily: "'Inter', sans-serif" }}>
          © {new Date().getFullYear()} <span style={{ color: "#1C3A5E", fontWeight: 700 }}>builtbyd</span> Freelance Studio. All rights reserved.{" "}
          <button onClick={() => setShowTerms(true)} style={{ background: "none", border: "none", color: "#B8904A", fontFamily: "'Inter', sans-serif", fontSize: "11px", fontWeight: 600, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
            Terms of Use
          </button>
        </p>
      </div>

      {/* Support Modal */}
      {showSupport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(13,31,60,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <div style={{ borderRadius: "28px", width: "100%", maxWidth: "480px", overflow: "hidden", boxShadow: "0 24px 64px rgba(13,31,60,0.25)", background: "rgba(255,255,255,0.97)", border: "1px solid rgba(255,255,255,0.95)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ background: "linear-gradient(135deg, #0D2137 0%, #1C3A5E 100%)", padding: "22px 26px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 700, color: "#fff", lineHeight: 1, marginBottom: "4px" }}>
                  💛 Support College <span style={{ color: "#F0C060" }}>Costs</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontFamily: "'Inter', sans-serif" }}>Free for students. Sustained by counselors who care.</div>
              </div>
              <button onClick={() => { setShowSupport(false); setSupportSubmitted(false); setSupportDiscordFailed(false); setSupportForm({ name: "", email: "", school: "", role: "", heard: "", amount: "" }); }}
                style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontSize: "16px", cursor: "pointer", lineHeight: 1, padding: "4px 10px", borderRadius: "8px", fontWeight: 700, flexShrink: 0 }}>×</button>
            </div>
            {supportSubmitted ? (
              <div style={{ padding: "44px 28px", textAlign: "center" }}>
                <div style={{ fontSize: "52px", marginBottom: "16px" }}>🎉</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 700, color: "#0D2137", marginBottom: "10px" }}>Thank you!</div>
                <p style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.7, fontFamily: "'Inter', sans-serif", maxWidth: "320px", margin: "0 auto 20px" }}>
                  Your response has been received. If you chose to donate, a payment link will be sent to your email. Every contribution keeps this tool free for students.
                </p>
                {supportDiscordFailed && (
                  <div style={{ margin: "0 auto 20px", maxWidth: "320px", background: "#FEF2F2", border: "1.5px solid #EF444440", borderRadius: "10px", padding: "12px 16px" }}>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#991B1B", marginBottom: "4px", fontFamily: "'Inter', sans-serif" }}>⚠️ Heads up</div>
                    <p style={{ margin: 0, fontSize: "12px", color: "#7F1D1D", lineHeight: 1.6, fontFamily: "'Inter', sans-serif" }}>
                      Your response was received but we had trouble sending the notification. Please email your details directly to <strong>dechellebuckhalter@gmail.com</strong> so nothing gets missed.
                    </p>
                  </div>
                )}
                <button onClick={() => { setShowSupport(false); setSupportSubmitted(false); setSupportDiscordFailed(false); setSupportForm({ name: "", email: "", school: "", role: "", heard: "", amount: "" }); }}
                  style={{ padding: "10px 28px", borderRadius: "12px", border: "none", background: "#0D2137", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                  Close
                </button>
              </div>
            ) : (
              <div style={{ padding: "24px 28px 28px" }}>
                <p style={{ fontSize: "12px", color: "#64748b", fontFamily: "'Inter', sans-serif", lineHeight: 1.7, marginBottom: "22px", padding: "12px 14px", background: "#f0f7ff", borderRadius: "10px", border: "1px solid #bfdbfe" }}>
                  📊 This form helps track who's using College Costs and where it's making an impact. Your info will never be shared or sold.
                </p>
                {[
                  { label: "Your name", key: "name", type: "text", placeholder: "Jane Smith" },
                  { label: "Email address", key: "email", type: "email", placeholder: "you@school.edu" },
                  { label: "School or organization", key: "school", type: "text", placeholder: "Beechcroft High School" },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: "14px" }}>
                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px", fontFamily: "'Inter', sans-serif" }}>{label}</label>
                    <input type={type} placeholder={placeholder}
                      value={supportForm[key]}
                      onChange={e => setSupportForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontFamily: "'Inter', sans-serif", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "#f8fafc", color: "#1e293b" }}
                    />
                  </div>
                ))}
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px", fontFamily: "'Inter', sans-serif" }}>Your role</label>
                  <select value={supportForm.role} onChange={e => setSupportForm(f => ({ ...f, role: e.target.value }))}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontFamily: "'Inter', sans-serif", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "#f8fafc", color: "#1e293b" }}>
                    <option value="">Select a role...</option>
                    <option>School counselor</option>
                    <option>College access advisor</option>
                    <option>Teacher</option>
                    <option>Administrator</option>
                    <option>Nonprofit / program staff</option>
                    <option>Parent</option>
                    <option>Student</option>
                    <option>Other</option>
                  </select>
                </div>
                <div style={{ marginBottom: "14px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px", fontFamily: "'Inter', sans-serif" }}>How did you hear about this tool?</label>
                  <select value={supportForm.heard} onChange={e => setSupportForm(f => ({ ...f, heard: e.target.value }))}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontFamily: "'Inter', sans-serif", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "#f8fafc", color: "#1e293b" }}>
                    <option value="">Select one...</option>
                    <option>A colleague shared it with me</option>
                    <option>Beechcroft High School</option>
                    <option>Social media</option>
                    <option>Google search</option>
                    <option>Email / newsletter</option>
                    <option>School or district announcement</option>
                    <option>Other</option>
                  </select>
                </div>
                <div style={{ marginBottom: "22px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "8px", fontFamily: "'Inter', sans-serif" }}>Donation amount (optional)</label>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                    {["$5", "$10", "$25", "$50", "Other", "Not now"].map(amt => (
                      <button key={amt} onClick={() => setSupportForm(f => ({ ...f, amount: amt }))}
                        style={{ padding: "7px 14px", borderRadius: "8px", border: supportForm.amount === amt ? "2px solid #0D2137" : "1.5px solid #e2e8f0", background: supportForm.amount === amt ? "#0D2137" : "#f8fafc", color: supportForm.amount === amt ? "#F0C060" : "#374151", fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 700, cursor: "pointer", transition: "all 0.15s" }}>
                        {amt}
                      </button>
                    ))}
                  </div>
                  <p style={{ margin: 0, fontSize: "10px", color: "#94a3b8", fontFamily: "'Inter', sans-serif" }}>
                    If you choose to donate, a payment link will be sent to your email after submitting.
                  </p>
                </div>
                <button
                  disabled={supportSubmitting || !supportForm.name || !supportForm.email || !supportForm.role}
                  onClick={async () => {
                    if (!supportForm.name || !supportForm.email || !supportForm.role) return;
                    setSupportSubmitting(true);
                    const DISCORD_WEBHOOK = "https://discord.com/api/webhooks/1481815648027021374/hjqUh3R0Dti1zwaJ019wz1ckxd8adFmvLfFGCx97oPP68iL4x4fbcvg99mFmuedlFA1y";
                    const payload = {
                      name: supportForm.name, email: supportForm.email,
                      school: supportForm.school || "Not provided", role: supportForm.role,
                      heard: supportForm.heard || "Not specified",
                      donation_amount: supportForm.amount || "Not specified",
                      submitted_at: new Date().toLocaleString(),
                    };
                    let discordOk = false;
                    try {
                      const res = await fetch(DISCORD_WEBHOOK, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          username: "College Costs",
                          avatar_url: "https://cdn.jsdelivr.net/gh/twitter/twemoji@14/assets/72x72/1f393.png",
                          embeds: [{ title: "📋 New Support Form Submission", color: 0xE8B84B,
                            fields: [
                              { name: "👤 Name", value: payload.name, inline: true },
                              { name: "📧 Email", value: payload.email, inline: true },
                              { name: "🏫 School", value: payload.school, inline: false },
                              { name: "💼 Role", value: payload.role, inline: true },
                              { name: "📣 How they heard", value: payload.heard, inline: true },
                              { name: "💛 Donation", value: payload.donation_amount, inline: true },
                            ],
                            footer: { text: `Submitted: ${payload.submitted_at}` },
                          }]
                        }),
                      });
                      discordOk = res.ok;
                    } catch(e) { discordOk = false; }
                    setSupportDiscordFailed(!discordOk);
                    setSupportSubmitting(false);
                    setSupportSubmitted(true);
                  }}
                  style={{ width: "100%", padding: "13px", borderRadius: "12px", border: "none", background: supportForm.name && supportForm.email && supportForm.role ? "linear-gradient(135deg, #0D2137, #1C3A5E)" : "#e2e8f0", color: supportForm.name && supportForm.email && supportForm.role ? "#F0C060" : "#94a3b8", fontFamily: "'Inter', sans-serif", fontSize: "14px", fontWeight: 800, cursor: supportForm.name && supportForm.email && supportForm.role ? "pointer" : "default", transition: "all 0.2s", letterSpacing: "0.02em" }}>
                  {supportSubmitting ? "Sending..." : "Submit ✦"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Request Access Modal */}
      {showRequestAccess && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(13,31,60,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <div style={{ borderRadius: "28px", width: "100%", maxWidth: "460px", overflow: "hidden", boxShadow: "0 24px 64px rgba(13,31,60,0.25)", background: "rgba(255,255,255,0.97)", border: "1px solid rgba(255,255,255,0.95)" }}>
            <div style={{ background: "linear-gradient(135deg, #0D2137 0%, #1C3A5E 100%)", padding: "20px 26px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "22px", fontWeight: 400, color: "#fff", lineHeight: 1 }}>
                  College <span style={{ color: "#F0C060", fontStyle: "italic" }}>Costs</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", marginTop: "4px", fontFamily: "'Inter', sans-serif" }}>Request counselor access</div>
              </div>
              <button onClick={() => { setShowRequestAccess(false); setRequestSubmitted(false); }} style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "#fff", fontSize: "16px", cursor: "pointer", lineHeight: 1, padding: "4px 10px", borderRadius: "8px", fontWeight: 700 }}>×</button>
            </div>
            {requestSubmitted ? (
              <div style={{ padding: "40px 28px", textAlign: "center" }}>
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>🎓</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "18px", fontWeight: 700, color: "#0D2137", marginBottom: "10px" }}>Request received!</div>
                <p style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.7, fontFamily: "'Inter', sans-serif", maxWidth: "300px", margin: "0 auto 24px" }}>
                  You'll receive an email within 1–2 business days with access instructions. Thank you for supporting your students.
                </p>
                <button onClick={() => { setShowRequestAccess(false); setRequestSubmitted(false); }} style={{ padding: "10px 28px", borderRadius: "12px", border: "none", background: "#0D2137", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>Done</button>
              </div>
            ) : (
              <div style={{ padding: "24px 28px 28px" }}>
                <p style={{ fontSize: "12px", color: "#64748b", fontFamily: "'Inter', sans-serif", lineHeight: 1.6, marginBottom: "20px" }}>
                  College Costs is free for counselors and students. Fill out the form below and you'll receive access within 1–2 business days.
                </p>
                {[
                  { label: "Full name", key: "name", type: "text", placeholder: "Jane Smith" },
                  { label: "School or organization", key: "school", type: "text", placeholder: "Beechcroft High School" },
                  { label: "Work email", key: "email", type: "email", placeholder: "you@school.edu" },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: "14px" }}>
                    <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px", fontFamily: "'Inter', sans-serif" }}>{label}</label>
                    <input type={type} placeholder={placeholder}
                      value={requestForm[key]}
                      onChange={e => setRequestForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontFamily: "'Inter', sans-serif", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "#f8fafc", color: "#1e293b" }}
                    />
                  </div>
                ))}
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#64748b", marginBottom: "5px", fontFamily: "'Inter', sans-serif" }}>Your role</label>
                  <select value={requestForm.role} onChange={e => setRequestForm(f => ({ ...f, role: e.target.value }))}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontFamily: "'Inter', sans-serif", fontSize: "13px", outline: "none", boxSizing: "border-box", background: "#f8fafc", color: "#1e293b" }}>
                    <option value="">Select a role...</option>
                    <option value="School counselor">School counselor</option>
                    <option value="College access advisor">College access advisor</option>
                    <option value="Teacher">Teacher</option>
                    <option value="Administrator">Administrator</option>
                    <option value="Nonprofit / program staff">Nonprofit / program staff</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div style={{ marginBottom: "20px", padding: "12px 16px", background: "#f0f7ff", borderRadius: "10px", border: "1px solid #bfdbfe" }}>
                  <p style={{ margin: 0, fontSize: "11px", color: "#1e40af", fontFamily: "'Inter', sans-serif", lineHeight: 1.6 }}>
                    💛 This tool is free and always will be for students. If it helps your work, a small donation keeps it running — but access is never conditional on it.
                  </p>
                </div>
                <button
                  onClick={() => { if (!requestForm.name || !requestForm.email || !requestForm.school || !requestForm.role) return; setRequestSubmitted(true); }}
                  style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "none", background: requestForm.name && requestForm.email && requestForm.school && requestForm.role ? "#0D2137" : "#e2e8f0", color: requestForm.name && requestForm.email && requestForm.school && requestForm.role ? "#fff" : "#94a3b8", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }}>
                  Submit request
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Terms of Use Modal */}
      {showTerms && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(28,58,94,0.25)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <div style={{ borderRadius: "28px", width: "100%", maxWidth: "520px", overflow: "hidden", boxShadow: "0 24px 64px rgba(28,58,94,0.18)", background: "rgba(255,255,255,0.96)", backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)", border: "1px solid rgba(255,255,255,0.95)" }}>
            <div style={{ background: "linear-gradient(135deg, #1C3A5E 0%, #2A5280 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ color: "#F0C060", fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "16px" }}>Terms of Use</div>
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>College Costs — builtbyd Freelance Studio</div>
              </div>
              <button onClick={() => setShowTerms(false)} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: "18px", cursor: "pointer", lineHeight: 1, padding: "4px 10px", borderRadius: "8px", fontWeight: 700 }}>×</button>
            </div>
            <div style={{ padding: "24px 28px", maxHeight: "60vh", overflowY: "auto" }}>
              {[
                { title: "1. Ownership", body: "College Costs is an original application created and owned by builtbyd Freelance Studio. All intellectual property rights, including design, code, concept, and branding, are retained solely by the creator. No use of this application transfers any ownership rights to any party." },
                { title: "2. Permitted Use", body: "This tool is provided for personal, non-commercial, educational use only. You may use it to estimate and compare college costs for your own decision-making purposes." },
                { title: "3. Prohibited Use", body: "You may not copy, reproduce, reverse engineer, distribute, sell, sublicense, or create derivative works from this application or any part of it without prior written consent from builtbyd Freelance Studio." },
                { title: "4. No Warranty", body: "This tool is provided 'as is' without warranty of any kind. Results are estimates based on user-entered data. Always verify costs directly with each school's financial aid office." },
                { title: "5. Disclaimer", body: "College Costs is not affiliated with any college, university, government agency, or financial institution. The creator is not a licensed financial advisor. This tool does not constitute financial advice." },
                { title: "6. Institutional Use", body: "Use of this application by schools, organizations, or institutions requires a separate written agreement with builtbyd Freelance Studio. Unauthorized institutional use is prohibited." },
                { title: "7. Contact", body: "For licensing inquiries or permissions, contact builtbyd Freelance Studio directly." },
              ].map(({ title, body }) => (
                <div key={title} style={{ marginBottom: "20px" }}>
                  <div style={{ fontSize: "12px", fontWeight: 800, color: "#1C3A5E", fontFamily: "'Inter', sans-serif", letterSpacing: "0.04em", marginBottom: "6px" }}>{title}</div>
                  <p style={{ margin: 0, fontSize: "12px", color: "#64748b", lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>{body}</p>
                </div>
              ))}
              <div style={{ marginTop: "8px", padding: "12px 16px", background: "#f8f4f0", borderRadius: "12px", border: "1px solid rgba(28,58,94,0.1)" }}>
                <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8", fontFamily: "'Inter', sans-serif", textAlign: "center" }}>
                  © {new Date().getFullYear()} builtbyd Freelance Studio. All rights reserved.
                </p>
              </div>
            </div>
            <div style={{ padding: "16px 28px 24px", textAlign: "center" }}>
              <button onClick={() => setShowTerms(false)} style={{ padding: "10px 32px", borderRadius: "14px", border: "none", background: "linear-gradient(135deg, #1C3A5E, #2A5280)", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(28,58,94,0.25)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <div style={{ borderRadius: "28px", width: "100%", maxWidth: "480px", overflow: "hidden", boxShadow: "0 24px 64px rgba(28,58,94,0.18), inset 0 1px 0 rgba(255,255,255,1)", background: "rgba(255,255,255,0.92)", backdropFilter: "blur(40px)", WebkitBackdropFilter: "blur(40px)", border: "1px solid rgba(255,255,255,0.95)" }}>
            <div style={{ background: "linear-gradient(135deg, #1C3A5E 0%, #2A5280 100%)", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "6px" }}>
                {ONBOARD_STEPS.map((_, i) => (
                  <div key={i} style={{ width: i === onboardStep ? "20px" : "8px", height: "8px", borderRadius: "4px", background: i === onboardStep ? "#F0C060" : "rgba(255,255,255,0.35)", transition: "all 0.3s" }} />
                ))}
              </div>
              <button onClick={dismissOnboarding} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", fontSize: "16px", cursor: "pointer", lineHeight: 1, padding: "4px 8px", borderRadius: "8px", fontWeight: 700 }}>×</button>
            </div>
            <div style={{ padding: "32px 28px 20px", background: "rgba(255,255,255,0.95)" }}>
              <div style={{ fontSize: "52px", marginBottom: "14px", textAlign: "center" }}>{ONBOARD_STEPS[onboardStep].emoji}</div>
              <h2 style={{ margin: "0 0 10px", fontSize: "20px", fontWeight: 900, color: "#1C3A5E", fontFamily: "'Playfair Display', serif", textAlign: "center", lineHeight: 1.3 }}>
                {ONBOARD_STEPS[onboardStep].title}
              </h2>
              <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#374151", lineHeight: 1.7, textAlign: "center" }}>
                {ONBOARD_STEPS[onboardStep].body}
              </p>
              {ONBOARD_STEPS[onboardStep].tip && (
                <div style={{ background: "#F0F7FF", border: "1.5px solid #F0C06060", borderRadius: "12px", padding: "10px 14px", marginBottom: "8px" }}>
                  <span style={{ fontSize: "12px", color: "#1C3A5E", fontWeight: 700 }}>💡 {ONBOARD_STEPS[onboardStep].tip}</span>
                </div>
              )}
            </div>
            <div style={{ padding: "0 28px 28px", display: "flex", gap: "10px", justifyContent: "space-between", alignItems: "center", background: "rgba(255,255,255,0.95)" }}>
              <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 600 }}>{onboardStep + 1} of {ONBOARD_STEPS.length}</span>
              <div style={{ display: "flex", gap: "8px" }}>
                {onboardStep > 0 && (
                  <button onClick={() => setOnboardStep(onboardStep - 1)}
                    style={{ padding: "10px 18px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#64748b", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                    Back
                  </button>
                )}
                {onboardStep < ONBOARD_STEPS.length - 1 ? (
                  <button onClick={() => setOnboardStep(onboardStep + 1)}
                    style={{ padding: "10px 22px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg, #1C3A5E, #2A5280)", color: "#fff", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 12px rgba(28,58,94,0.3)" }}>
                    Next →
                  </button>
                ) : (
                  <button onClick={dismissOnboarding}
                    style={{ padding: "10px 22px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg, #1C3A5E, #2A5280)", color: "#F0C060", fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 12px rgba(28,58,94,0.3)" }}>
                    Let's Go! 🎉
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CRow({ label, value, bold, muted, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "7px" }}>
      <span style={{ fontSize: "12px", color: muted ? "#94a3b8" : bold ? "#374151" : "#6B7280", fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: color || (bold ? "#111827" : "#374151"), fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}
function SumRow({ label, value, bold, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
      <span style={{ fontSize: "12px", color: bold ? "#374151" : "#6B7280", fontWeight: bold ? 700 : 400 }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: color || (bold ? "#111827" : "#374151"), fontWeight: bold ? 700 : 500 }}>{value}</span>
    </div>
  );
}
function FourYearStat({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
      <span style={{ fontSize: "12px", color: "#6B7280" }}>{label}</span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "13px", color, fontWeight: 600 }}>{value}</span>
    </div>
  );
}
function Disclaimer() {
  return (
    <div style={{ marginTop: "16px", background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: "10px", padding: "12px 16px" }}>
      <p style={{ margin: 0, fontSize: "11px", color: "#94a3b8", lineHeight: 1.7 }}>
        📌 These are estimates based on the information you enter. Actual costs may vary. Always verify directly with the school's financial aid office. Loans shown here must be repaid — they are not grants or scholarships.
      </p>
    </div>
  );
}